<template>
  <div class="review-notes" :class="{ 'review-notes--mobile': isMobile }" v-loading="pageLoading">
    <section class="panel toolbar-panel" :class="{ 'toolbar-panel--mobile': isMobile }">
      <div class="panel-title">查询区间</div>
      <div class="toolbar" :class="{ 'toolbar--mobile': isMobile }">
        <template v-if="!isMobile">
          <span class="toolbar-label">开始</span>
          <el-date-picker v-model="startDate" type="date" value-format="yyyy-MM-dd" :size="inputSize" class="toolbar-date" />
          <span class="toolbar-label">结束</span>
          <el-date-picker v-model="endDate" type="date" value-format="yyyy-MM-dd" :size="inputSize" class="toolbar-date" />
          <el-button type="primary" :size="inputSize" :loading="queryLoading" @click="handleQuery">查询</el-button>
          <el-button type="primary" :size="inputSize" :loading="syncLoading" @click="handleSyncRange">区间同步</el-button>
        </template>
        <template v-else>
          <div class="toolbar-grid">
            <div class="toolbar-field">
              <span class="toolbar-label">开始</span>
              <el-date-picker v-model="startDate" type="date" value-format="yyyy-MM-dd" size="small" class="toolbar-date toolbar-date--fluid" />
            </div>
            <div class="toolbar-field">
              <span class="toolbar-label">结束</span>
              <el-date-picker v-model="endDate" type="date" value-format="yyyy-MM-dd" size="small" class="toolbar-date toolbar-date--fluid" />
            </div>
          </div>
          <div class="toolbar-actions">
            <el-button type="primary" size="small" :loading="queryLoading" @click="handleQuery">查询</el-button>
            <el-button type="primary" size="small" plain :loading="syncLoading" @click="handleSyncRange">区间同步</el-button>
          </div>
        </template>
      </div>
    </section>

    <div v-if="hasQueried" class="main-layout">
      <section class="panel content-panel">
        <div class="panel-head">
          <div class="panel-title">情绪走势</div>
          <div v-if="selectedTradeDate && !isMobile" class="panel-sub">当前选中 {{ selectedTradeDate }}</div>
        </div>

        <div v-if="isMobile" class="chart-tab-pills">
          <button
            v-for="tab in chartTabs"
            :key="tab.key"
            type="button"
            class="chart-tab-pill"
            :class="{ 'is-active': activeChartTab === tab.key }"
            @click="setChartTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>
        <div ref="chartRef" class="charts-area" :class="{ 'charts-area--mobile': isMobile }" />

        <div v-if="isMobile" class="mobile-day-nav">
          <button type="button" class="day-nav-btn" :disabled="!canPrevDay" @click="shiftDay(-1)">
            <i class="el-icon-arrow-left" />
          </button>
          <div class="mobile-day-center">
            <span class="mobile-day-label">{{ selectedTradeDate || '-' }}</span>
            <span v-if="selectedDateIndex >= 0" class="mobile-day-hint">{{ mobileSelectedMetricText }}</span>
          </div>
          <button type="button" class="day-nav-btn" :disabled="!canNextDay" @click="shiftDay(1)">
            <i class="el-icon-arrow-right" />
          </button>
        </div>

        <div
          class="panel-head panel-head--table"
          :class="{ 'panel-head--collapsible': isMobile }"
          @click="isMobile && toggleMobileReview()"
        >
          <div class="panel-title-row">
            <div class="panel-title">复盘记录</div>
            <button
              type="button"
              class="record-library-btn"
              @click.stop="openRecordLibraryDialog"
            >
              <i class="el-icon-notebook-2" />
              <span>记录库</span>
            </button>
          </div>
          <div v-if="isMobile" class="panel-collapse-ctrl">
            <span class="panel-collapse-hint">{{ mobileReviewExpanded ? '收起' : `共 ${series.tradeDates.length} 天` }}</span>
            <i :class="mobileReviewExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          </div>
          <div v-else class="panel-hint">单击选日期 · 双击编辑 · 复盘备注可查看，仅本人可编辑</div>
        </div>

        <div v-if="!isMobile" class="review-table-wrap">
          <table class="review-table">
            <thead>
              <tr>
                <th class="review-table-label-col">字段</th>
                <th
                  v-for="(td, idx) in series.tradeDates"
                  :key="td"
                  :class="{ 'is-selected': td === selectedTradeDate }"
                  @click="selectDate(td, idx)"
                >
                  <span class="date-head">{{ formatShortDate(td) }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="row.key">
                <td class="review-table-label-col">
                  <span v-if="row.showGreedyNodeHelp" class="review-label-with-help">
                    {{ row.label }}
                    <el-popover
                      placement="right"
                      width="420"
                      trigger="hover"
                      popper-class="greedy-node-catalog-popover"
                    >
                      <div class="greedy-node-catalog">
                        <div class="greedy-node-catalog__head">{{ greedyNodeSchemeTitle }}</div>
                        <div
                          v-for="node in series.greedyNodeCatalog"
                          :key="node.id"
                          class="greedy-node-catalog__item"
                        >
                          <div class="greedy-node-catalog__title">
                            <strong>{{ node.id }}</strong>
                            <span class="greedy-node-catalog__weight">(+{{ node.weight }})</span>
                            <span class="greedy-node-catalog__factor">{{ node.factor }}</span>
                          </div>
                          <div class="greedy-node-catalog__rule">{{ node.rule }}</div>
                        </div>
                      </div>
                      <i slot="reference" class="el-icon-question greedy-node-help-icon" @click.stop />
                    </el-popover>
                  </span>
                  <template v-else>{{ row.label }}</template>
                </td>
                <td
                  v-for="(td, idx) in series.tradeDates"
                  :key="`${row.key}-${td}`"
                  :class="{
                    'is-selected': td === selectedTradeDate,
                    'is-empty': row.getDisplay(idx) === '-',
                    'is-cart-link': row.opensCart && canEditCartOwner(row.cartOwner),
                    'is-cart-readonly': row.opensCart && !canEditCartOwner(row.cartOwner),
                    'is-emphasis': row.redBold && row.getDisplay(idx) !== '-'
                  }"
                  @click.stop="row.opensCart ? openCartDialogForDate(td, idx, row.key) : selectDate(td, idx)"
                  @dblclick.stop="!row.opensCart && openNoteDialog(td)"
                >
                  <template v-if="row.key === 'nodeStatus'">
                    <span v-if="!getNodeTokensForIndex(idx).length" class="review-table-cell-text">-</span>
                    <span v-else class="review-table-node-cell">
                      <span
                        v-for="(node, ni) in getNodeTokensForIndex(idx)"
                        :key="`${td}-${node}-${ni}`"
                        class="review-table-node-part"
                        :class="getNodeClass(node)"
                      >{{ node }}<template v-if="ni < getNodeTokensForIndex(idx).length - 1">，</template></span>
                    </span>
                  </template>
                  <template v-else-if="row.key === 'greedyNodeHits'">
                    <span v-if="!getGreedyNodeHitsForIndex(idx).length" class="review-table-cell-text">-</span>
                    <span v-else class="review-table-greedy-nodes is-hit">
                      <span v-for="(nodeId, ni) in getGreedyNodeHitsForIndex(idx)" :key="`${td}-${nodeId}`">
                        <el-tooltip placement="top" :open-delay="200" popper-class="greedy-node-hit-tooltip">
                          <div slot="content" class="greedy-node-tooltip-content">
                            <div class="greedy-node-tooltip-title">
                              {{ nodeId }} (+{{ getGreedyNodeWeight(nodeId) }}) · {{ getGreedyNodeFactor(nodeId) }}
                            </div>
                            <div>{{ getGreedyNodeRule(nodeId) }}</div>
                          </div>
                          <span class="greedy-node-hit-tag">{{ formatGreedyNodeHitLabel(nodeId) }}</span>
                        </el-tooltip>
                        <template v-if="ni < getGreedyNodeHitsForIndex(idx).length - 1">、</template>
                      </span>
                    </span>
                  </template>
                  <span v-else class="review-table-cell-text">{{ row.getDisplay(idx) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="mobileReviewExpanded" class="mobile-cards">
          <div
            v-for="(td, idx) in series.tradeDates"
            :key="td"
            class="day-card"
            :class="{ 'is-selected': td === selectedTradeDate }"
            @click="selectDate(td, idx)"
          >
            <div class="day-card__head">
              <span class="day-card__date">{{ formatShortDate(td) }}</span>
              <span class="day-card__full">{{ td }}</span>
            </div>
            <div class="day-card__metrics">
              <span class="metric-chip metric-chip--temp">
                <em>温度</em>{{ fmtNum(series.temperature[idx]) }}
              </span>
              <span class="metric-chip metric-chip--height">
                <em>连板</em>{{ fmtNum(series.boardHeightDisplay[idx]) }}
              </span>
              <span v-if="series.marketIndexDisplay[idx] != null" class="metric-chip metric-chip--index">
                <em>指数</em>{{ fmtPct(series.marketIndexDisplay[idx]) }}
              </span>
            </div>
            <div v-if="mobileCardNotePreview(idx)" class="day-card__note">{{ mobileCardNotePreview(idx) }}</div>
            <button type="button" class="day-card__edit" @click.stop="openMobileDrawer(td)">
              <i class="el-icon-edit-outline" /> 编辑
            </button>
          </div>
        </div>

        <div v-else-if="isMobile && !mobileReviewExpanded" class="mobile-review-collapsed">
          <div
            v-if="selectedDateIndex >= 0"
            class="day-card day-card--compact"
            @click="openMobileDrawer(series.tradeDates[selectedDateIndex])"
          >
            <div class="day-card__head">
              <span class="day-card__date">{{ formatShortDate(series.tradeDates[selectedDateIndex]) }}</span>
              <span class="day-card__full">{{ series.tradeDates[selectedDateIndex] }}</span>
            </div>
            <div class="day-card__metrics">
              <span class="metric-chip metric-chip--temp">
                <em>温度</em>{{ fmtNum(series.temperature[selectedDateIndex]) }}
              </span>
              <span class="metric-chip metric-chip--height">
                <em>连板</em>{{ fmtNum(series.boardHeightDisplay[selectedDateIndex]) }}
              </span>
            </div>
            <div v-if="mobileCardNotePreview(selectedDateIndex)" class="day-card__note">
              {{ mobileCardNotePreview(selectedDateIndex) }}
            </div>
          </div>
          <button type="button" class="mobile-review-expand-btn" @click.stop="mobileReviewExpanded = true">
            展开全部 {{ series.tradeDates.length }} 天
            <i class="el-icon-arrow-down" />
          </button>
        </div>
      </section>

      <section class="panel ladder-panel" :class="{ 'ladder-panel--has-cart': isMobile && cartCount > 0 }">
        <div class="panel-head">
          <div class="panel-title">涨停天梯</div>
          <div class="panel-head-right">
            <button
              v-if="selectedTradeDate"
              type="button"
              class="cart-trigger"
              @click="openCartDialog"
            >
              <i class="el-icon-shopping-cart-2" />
              <span>购物车</span>
              <span v-if="cartCount" class="cart-badge">{{ cartCount }}</span>
              <span v-if="cartDirty" class="cart-dirty-dot" title="有未保存改动" />
            </button>
            <div v-if="selectedTradeDate" class="panel-sub">{{ selectedTradeDate }}</div>
          </div>
        </div>

        <div class="ladder-toolbar">
          <div class="ladder-filters" :class="{ 'ladder-filters--mobile': isMobile }">
            <template v-if="isMobile">
              <button
                type="button"
                class="filter-chip"
                :class="{ 'is-active': onlyMainBoard }"
                @click="onlyMainBoard = !onlyMainBoard"
              >只看主板</button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterMarketValue }"
                @click="filterMarketValue = !filterMarketValue"
              >过滤市值{{ marketCapMaxDisplay }}亿</button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterMa250 }"
                @click="toggleFilterMa250"
              >过滤MA250</button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterDistToYearHigh }"
                @click="filterDistToYearHigh = !filterDistToYearHigh"
              >距年高≤{{ distToYearHighMaxDisplay }}%</button>
              <button
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterPreScreenScore }"
                @click="filterPreScreenScore = !filterPreScreenScore"
              >综合分≥{{ preScreenScoreMinDisplay }}</button>
              <button type="button" class="filter-chip" @click="openFilterConfig">过滤配置</button>
              <span class="filter-rel-vol-label">短期</span>
              <button
                v-for="opt in shortChipOptions"
                :key="`m-short-${opt.value}`"
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterShortChipBands.includes(opt.value) }"
                @click="toggleChipBand('short', opt.value)"
              >{{ opt.label }}</button>
              <span class="filter-rel-vol-label">中期</span>
              <button
                v-for="opt in midChipOptions"
                :key="`m-mid-${opt.value}`"
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterMidChipBands.includes(opt.value) }"
                @click="toggleChipBand('mid', opt.value)"
              >{{ opt.label }}</button>
              <span class="filter-rel-vol-label">长期</span>
              <button
                v-for="opt in longChipOptions"
                :key="`m-long-${opt.value}`"
                type="button"
                class="filter-chip"
                :class="{ 'is-active': filterLongChipBands.includes(opt.value) }"
                @click="toggleChipBand('long', opt.value)"
              >{{ opt.label }}</button>
            </template>
            <template v-else>
              <el-button size="mini" @click="openFilterConfig">过滤配置</el-button>
              <el-checkbox v-model="onlyMainBoard" size="mini" border>只看主板</el-checkbox>
              <el-checkbox v-model="filterMarketValue" size="mini" border>过滤市值{{ marketCapMaxDisplay }}亿</el-checkbox>
              <el-checkbox v-model="filterMa250" size="mini" border @change="onFilterMa250Change">过滤MA250</el-checkbox>
              <el-checkbox v-model="filterDistToYearHigh" size="mini" border>
                距年高 ≤ {{ distToYearHighMaxDisplay }}%
              </el-checkbox>
              <el-checkbox v-model="filterPreScreenScore" size="mini" border>
                综合分 ≥ {{ preScreenScoreMinDisplay }}
              </el-checkbox>
              <span class="filter-rel-vol-label">短期筹码</span>
              <el-select
                v-model="filterShortChipBands"
                multiple
                collapse-tags
                clearable
                size="mini"
                placeholder="多选"
                class="filter-chip-band-select"
              >
                <el-option v-for="opt in shortChipOptions" :key="'short-'+opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
              <span class="filter-rel-vol-label">中期筹码</span>
              <el-select
                v-model="filterMidChipBands"
                multiple
                collapse-tags
                clearable
                size="mini"
                placeholder="多选"
                class="filter-chip-band-select"
              >
                <el-option v-for="opt in midChipOptions" :key="'mid-'+opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
              <span class="filter-rel-vol-label">长期筹码</span>
              <el-select
                v-model="filterLongChipBands"
                multiple
                collapse-tags
                clearable
                size="mini"
                placeholder="多选"
                class="filter-chip-band-select"
              >
                <el-option v-for="opt in longChipOptions" :key="'long-'+opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
            </template>
          </div>
        </div>

        <div v-if="ladderThemes.length" class="theme-strip">
          <div
            v-for="theme in ladderThemes"
            :key="themeKey(theme)"
            class="theme-chip"
            :class="[themeChipClass(theme), { 'is-active': isThemeSelected(theme) }]"
            @click="selectTheme(theme)"
          >
            <span class="theme-chip-name">{{ theme.name }}</span>
            <span class="theme-chip-meta">{{ theme.count }}家{{ theme.turnoverYi != null ? ` · ${theme.turnoverYi}亿` : '' }}</span>
          </div>
        </div>

        <div v-loading="ladderLoading" class="ladder-body">
          <div v-for="group in ladderGroups" :key="group.boards" class="ladder-group">
            <div class="ladder-group-title">
              <span class="ladder-board-badge">{{ group.boards }}</span>
              <span>连板 · {{ group.stocks.length }} 家</span>
              <span v-if="group.nodeStars.length" class="node-star-mark">
                <span
                  v-for="(star, si) in group.nodeStars"
                  :key="`ladder-star-${group.boards}-${si}`"
                  :class="star.class"
                >★</span>
              </span>
            </div>
            <div class="ladder-stocks">
              <div
                v-for="stock in group.stocks"
                :key="stock.code"
                class="ladder-stock"
                :class="{ 'is-in-cart': isInCart(stock) }"
                @click="onLadderStockClick(stock)"
                @contextmenu.prevent="openLadderContextMenu($event, stock)"
                @touchstart.passive="onLadderTouchStart($event, stock)"
                @touchmove.passive="onLadderTouchMove"
                @touchend="onLadderTouchEnd"
                @touchcancel="onLadderTouchEnd"
              >
                <div v-if="isInCart(stock)" class="ladder-stock-check">
                  <i class="el-icon-check" />
                </div>
                <div class="ladder-stock-top">
                  <span class="ladder-stock-name" @click.stop>{{ stock.name }}</span>
                  <span
                    v-if="stock.preScreenScore != null"
                    class="ladder-stock-score"
                  >{{ formatPreScreenScore(stock.preScreenScore) }}</span>
                </div>
                <div class="ladder-stock-meta">
                  <span class="ladder-stock-theme">{{ stock.themeName || '—' }}</span>
                  <span class="ladder-stock-time">{{ formatLimitTime(stock.limitUpTime) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!ladderLoading && !ladderGroups.length" class="ladder-empty">
            <i class="el-icon-document" />
            <p>暂无天梯数据</p>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="!queryLoading" class="empty-placeholder">
      <i class="el-icon-data-line" />
      <p>选择日期区间后点击查询</p>
    </div>

    <el-dialog title="编辑" :visible.sync="noteDialogVisible" width="920px" custom-class="note-edit-dialog" :fullscreen="isMobile">
      <el-form v-if="editingNote" label-width="100px" size="small">
        <el-form-item label="连板高度">
          <el-input-number v-model="editingNote.boardHeightOverride" :min="0" :max="20" controls-position="right" />
        </el-form-item>
        <el-form-item label="次高连板">
          <el-input-number v-model="editingNote.boardHeightSecondOverride" :min="0" :max="20" controls-position="right" />
        </el-form-item>
        <el-form-item label="指数(%)">
          <el-input-number v-model="editingNote.marketIndexOverride" :precision="2" :step="0.01" controls-position="right" />
        </el-form-item>
        <el-form-item label="指数方向">
          <el-radio-group v-model="editingNote.indexTrend">
            <el-radio label="">不选</el-radio>
            <el-radio v-for="opt in indexTrendOptions" :key="opt" :label="opt">{{ opt }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="高度标签">
          <el-input v-model="editingNote.heightLabel" placeholder="如 4-顺钠" />
        </el-form-item>
        <el-form-item label="市场节点">
          <el-checkbox-group v-model="editingNote.selectedNodes" class="edit-node-group">
            <el-checkbox
              v-for="node in marketNodeOptions"
              :key="node"
              :label="node"
              class="edit-node-item edit-node-item--market"
            >
              {{ node }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="龙头节点">
          <el-checkbox-group v-model="editingNote.selectedNodes" class="edit-node-group">
            <el-checkbox
              v-for="node in dragonNodeOptions"
              :key="node"
              :label="node"
              class="edit-node-item edit-node-item--dragon"
            >
              {{ node }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-divider content-position="left">日因子（贪心V2）</el-divider>
        <div class="node-factor-toolbar">
          <span class="node-factor-meta">
            节点分
            <b>{{ editingNote.greedyNodeScore != null ? editingNote.greedyNodeScore : '—' }}</b>
            <template v-if="editingNote.greedyNodeHit"> ⬡</template>
          </span>
          <div class="node-factor-detail">
            <span>详细节点：</span>
            <template v-if="!editingNote.greedyNodeHits?.length">无</template>
            <template v-else>
              <span v-for="(nodeId, ni) in editingNote.greedyNodeHits" :key="nodeId">
                <el-tooltip placement="top" :open-delay="200" popper-class="greedy-node-hit-tooltip">
                  <div slot="content" class="greedy-node-tooltip-content">
                    <div class="greedy-node-tooltip-title">
                      {{ nodeId }} (+{{ getGreedyNodeWeight(nodeId) }}) · {{ getGreedyNodeFactor(nodeId) }}
                    </div>
                    <div>{{ getGreedyNodeRule(nodeId) }}</div>
                  </div>
                  <span class="greedy-node-hit-tag">{{ formatGreedyNodeHitLabel(nodeId) }}</span>
                </el-tooltip>
                <template v-if="ni < editingNote.greedyNodeHits.length - 1">、</template>
              </span>
            </template>
            <el-popover placement="top" width="420" trigger="hover" popper-class="greedy-node-catalog-popover">
              <div class="greedy-node-catalog">
                <div class="greedy-node-catalog__head">{{ greedyNodeSchemeTitle }}</div>
                <div v-for="node in series.greedyNodeCatalog" :key="node.id" class="greedy-node-catalog__item">
                  <div class="greedy-node-catalog__title">
                    <strong>{{ node.id }}</strong>
                    <span class="greedy-node-catalog__weight">(+{{ node.weight }})</span>
                    <span class="greedy-node-catalog__factor">{{ node.factor }}</span>
                  </div>
                  <div class="greedy-node-catalog__rule">{{ node.rule }}</div>
                </div>
              </div>
              <i slot="reference" class="el-icon-question greedy-node-help-icon" />
            </el-popover>
          </div>
        </div>
        <el-row :gutter="12" class="node-factor-grid">
          <el-col :span="8" v-for="item in nodeFactorEditFields" :key="item.key">
            <el-form-item :label="item.label" label-width="112px">
              <el-input-number
                v-model="editingNote.nodeFactors[item.key]"
                :precision="item.precision"
                :controls="false"
                class="node-factor-input"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="复盘节点高度">
          <el-input v-model="editingNote.reviewNodeHeight" placeholder="如 4-顺钠" />
        </el-form-item>
        <el-form-item label="情绪票爆量">
          <el-input v-model="editingNote.emotionVolumeNote" />
        </el-form-item>
        <el-form-item label="情绪票节点">
          <el-input v-model="editingNote.emotionNodeNote" />
        </el-form-item>
        <el-form-item label="连板标注">
          <el-input v-model="editingNote.chartAnnotations" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="noteDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saveNoteLoading" @click="saveEditingNote">保存</el-button>
      </span>
    </el-dialog>

    <el-drawer title="编辑" :visible.sync="mobileDrawerVisible" direction="btt" size="90%" custom-class="review-drawer">
      <div v-if="editingNote" class="mobile-drawer-body">
        <el-form label-position="top" size="small">
          <el-form-item label="连板高度">
            <el-input-number v-model="editingNote.boardHeightOverride" :min="0" :max="20" controls-position="right" />
          </el-form-item>
          <el-form-item label="次高连板">
            <el-input-number v-model="editingNote.boardHeightSecondOverride" :min="0" :max="20" controls-position="right" />
          </el-form-item>
          <el-form-item label="指数(%)">
            <el-input-number v-model="editingNote.marketIndexOverride" :precision="2" :step="0.01" controls-position="right" />
          </el-form-item>
          <el-form-item label="指数方向">
            <el-radio-group v-model="editingNote.indexTrend">
              <el-radio label="">不选</el-radio>
              <el-radio v-for="opt in indexTrendOptions" :key="opt" :label="opt">{{ opt }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="高度标签"><el-input v-model="editingNote.heightLabel" /></el-form-item>
          <el-form-item label="市场节点">
            <el-checkbox-group v-model="editingNote.selectedNodes" class="edit-node-group">
              <el-checkbox
                v-for="node in marketNodeOptions"
                :key="node"
                :label="node"
                class="edit-node-item edit-node-item--market"
              >
                {{ node }}
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-form-item label="龙头节点">
            <el-checkbox-group v-model="editingNote.selectedNodes" class="edit-node-group">
              <el-checkbox
                v-for="node in dragonNodeOptions"
                :key="node"
                :label="node"
                class="edit-node-item edit-node-item--dragon"
              >
                {{ node }}
              </el-checkbox>
            </el-checkbox-group>
          </el-form-item>
          <el-divider content-position="left">日因子（贪心V2）</el-divider>
          <div class="node-factor-toolbar">
            <span class="node-factor-meta">
              节点分
              <b>{{ editingNote.greedyNodeScore != null ? editingNote.greedyNodeScore : '—' }}</b>
              <template v-if="editingNote.greedyNodeHit"> ⬡</template>
            </span>
            <div class="node-factor-detail">
              <span>详细节点：</span>
              <template v-if="!editingNote.greedyNodeHits?.length">无</template>
              <template v-else>
                <span v-for="(nodeId, ni) in editingNote.greedyNodeHits" :key="nodeId">
                  <el-tooltip placement="top" :open-delay="200" popper-class="greedy-node-hit-tooltip">
                    <div slot="content" class="greedy-node-tooltip-content">
                      <div class="greedy-node-tooltip-title">
                        {{ nodeId }} (+{{ getGreedyNodeWeight(nodeId) }}) · {{ getGreedyNodeFactor(nodeId) }}
                      </div>
                      <div>{{ getGreedyNodeRule(nodeId) }}</div>
                    </div>
                    <span class="greedy-node-hit-tag">{{ formatGreedyNodeHitLabel(nodeId) }}</span>
                  </el-tooltip>
                  <template v-if="ni < editingNote.greedyNodeHits.length - 1">、</template>
                </span>
              </template>
              <el-popover placement="top" width="420" trigger="hover" popper-class="greedy-node-catalog-popover">
                <div class="greedy-node-catalog">
                  <div class="greedy-node-catalog__head">{{ greedyNodeSchemeTitle }}</div>
                  <div v-for="node in series.greedyNodeCatalog" :key="node.id" class="greedy-node-catalog__item">
                    <div class="greedy-node-catalog__title">
                      <strong>{{ node.id }}</strong>
                      <span class="greedy-node-catalog__weight">(+{{ node.weight }})</span>
                      <span class="greedy-node-catalog__factor">{{ node.factor }}</span>
                    </div>
                    <div class="greedy-node-catalog__rule">{{ node.rule }}</div>
                  </div>
                </div>
                <i slot="reference" class="el-icon-question greedy-node-help-icon" />
              </el-popover>
            </div>
          </div>
          <el-form-item v-for="item in nodeFactorEditFields" :key="item.key" :label="item.label">
            <el-input-number
              v-model="editingNote.nodeFactors[item.key]"
              :precision="item.precision"
              :controls="false"
              class="node-factor-input"
            />
          </el-form-item>
          <el-form-item label="复盘节点高度">
            <el-input v-model="editingNote.reviewNodeHeight" placeholder="如 4-顺钠" />
          </el-form-item>
          <el-form-item label="情绪票爆量"><el-input v-model="editingNote.emotionVolumeNote" /></el-form-item>
          <el-form-item label="情绪票节点"><el-input v-model="editingNote.emotionNodeNote" /></el-form-item>
          <el-form-item label="连板标注"><el-input v-model="editingNote.chartAnnotations" type="textarea" :rows="3" /></el-form-item>
        </el-form>
      </div>
      <div v-if="editingNote" class="mobile-drawer-footer">
        <el-button @click="mobileDrawerVisible = false">取消</el-button>
        <el-button type="primary" :loading="saveNoteLoading" @click="saveEditingNote">保存</el-button>
      </div>
    </el-drawer>

    <el-dialog
      :title="cartDialogTitle"
      :visible.sync="cartDialogVisible"
      width="720px"
      :fullscreen="isMobile"
      custom-class="review-cart-dialog"
      center
      append-to-body
      @close="onCartDialogClosed"
    >
      <div class="cart-dialog-body">
        <div v-if="selectedTradeDate" class="cart-dialog-date">{{ selectedTradeDate }}</div>
        <div v-if="cartReadOnly" class="cart-readonly-tip">
          只读：{{ cartOwnerLabel(cartOwner) }}的复盘笔记，仅本人可编辑
        </div>

        <section class="cart-section">
          <div class="cart-section-title">
            <span>1. 节点信息</span>
            <span v-if="cartColoredNodeStars.length" class="node-star-mark">
              <span
                v-for="(star, si) in cartColoredNodeStars"
                :key="`cart-star-${si}`"
                :class="star.class"
              >★</span>
            </span>
          </div>
          <div v-if="cartDisplayNodes.length" class="cart-node-tags">
            <span
              v-for="node in cartDisplayNodes"
              :key="node"
              class="cart-node-tag"
              :class="getNodeTagClass(node)"
            >{{ node }}</span>
          </div>
          <div v-else class="cart-node-empty">未选择节点，请在「编辑」中设置</div>
        </section>

        <section class="cart-section">
          <div class="cart-section-title">2. 预选个股</div>
          <div v-if="!cartCount" class="cart-empty cart-empty--inline">
            <i class="el-icon-shopping-cart-2" />
            <p>{{ cartReadOnly ? '暂无预选个股' : '点击天梯个股加入预选' }}</p>
          </div>
          <div v-else class="cart-ladder-body">
            <div v-for="group in cartLadderGroups" :key="group.boards" class="ladder-group ladder-group--cart">
              <div class="ladder-group-title">
                <span class="ladder-board-badge">{{ group.boards }}</span>
                <span>连板 · {{ group.stocks.length }} 家</span>
                <span v-if="group.nodeStars.length" class="node-star-mark">
                  <span
                    v-for="(star, si) in group.nodeStars"
                    :key="`cart-group-star-${group.boards}-${si}`"
                    :class="star.class"
                  >★</span>
                </span>
              </div>
              <div class="ladder-stocks">
                <div
                  v-for="item in group.stocks"
                  :key="item.code"
                  class="ladder-stock ladder-stock--cart"
                >
                  <div class="ladder-stock-top">
                    <span class="ladder-stock-name">{{ item.name }}</span>
                    <span v-if="!cartReadOnly" class="cart-item-remove cart-item-remove--inline" @click.stop="removeFromCart(item.code)">
                      <i class="el-icon-close" />
                    </span>
                  </div>
                  <div class="ladder-stock-meta">
                    <span class="ladder-stock-theme">{{ item.themeName || '—' }}</span>
                    <span class="ladder-stock-time">{{ formatLimitTime(item.limitUpTime) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="showPreFirstBoardSection" class="cart-section">
          <div class="cart-section-title cart-section-title--prefirst">
            <span>预首板 · {{ cartPreFirstBoardStocks.length }}</span>
            <el-button
              v-if="!cartReadOnly"
              type="primary"
              size="mini"
              plain
              @click="goToPreFirstBoardPick"
            >
              前往添加
            </el-button>
          </div>
          <div v-if="!cartPreFirstBoardStocks.length" class="cart-node-empty">暂无预首板个股</div>
          <div v-else class="cart-ladder-body">
            <div class="ladder-stocks">
              <div
                v-for="item in cartPreFirstBoardStocks"
                :key="item.code"
                class="ladder-stock ladder-stock--cart"
              >
                <div class="ladder-stock-top">
                  <span class="ladder-stock-name">{{ item.name }}</span>
                </div>
                <div class="ladder-stock-meta">
                  <span class="ladder-stock-theme">{{ item.themeName || '—' }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="cart-section">
          <div class="cart-section-title">3. 复盘思路<span class="cart-section-sub">{{ reviewThoughtHint }}</span></div>
          <el-input
            v-model="cartReviewThought"
            type="textarea"
            :rows="5"
            :disabled="cartReadOnly"
            :placeholder="reviewThoughtPlaceholder"
          />
        </section>
      </div>
      <span slot="footer" class="cart-dialog-footer">
        <el-button @click="cartDialogVisible = false">{{ cartReadOnly ? '关闭' : '取消' }}</el-button>
        <el-button
          v-if="!cartReadOnly"
          type="primary"
          :loading="saveCartLoading"
          :disabled="!cartDirty"
          @click="saveCartToDb"
        >
          保存
        </el-button>
      </span>
    </el-dialog>

    <!-- 天梯右键 / 长按菜单 -->
    <div
      v-if="ladderContextMenu.visible"
      class="ladder-context-menu"
      :style="{ left: `${ladderContextMenu.x}px`, top: `${ladderContextMenu.y}px` }"
      @click.stop
    >
      <button type="button" class="ladder-context-menu__item" @click="addStockToRecordLibrary">
        入记录库
      </button>
      <button type="button" class="ladder-context-menu__item" @click="addStockToAuctionTopLibrary('T0')">
        入TOP库 T0
      </button>
      <button type="button" class="ladder-context-menu__item" @click="addStockToAuctionTopLibrary('T1')">
        入TOP库 T1
      </button>
    </div>

    <el-dialog
      title="记录库"
      :visible.sync="recordLibraryVisible"
      width="95%"
      top="4vh"
      custom-class="record-library-dialog"
      append-to-body
      @open="onRecordLibraryDialogOpen"
    >
      <el-tabs v-model="recordLibraryTab">
        <el-tab-pane label="龙头库" name="dragon">
          <RecordLibraryTable
            :rows="recordLibraryList"
            :loading="recordLibraryLoading"
            editable
            :editing-id="recordLibraryEditingId"
            :initial-score-version="recordLibraryScoreVersion"
            :max-height="520"
            empty-text="暂无记录，可在涨停天梯右键/长按个股入库存，或在历史首板勾选入库"
            @score-version-change="onRecordLibraryScoreVersionChange"
            @start-edit="startRecordLibraryEdit"
            @save-edit="saveRecordLibraryEdit"
            @remove="removeRecordLibraryItem"
          />
        </el-tab-pane>
        <el-tab-pane label="补涨库" name="gapup">
          <GapUpRecordLibraryTable
            :rows="gapUpLibraryList"
            :loading="gapUpLibraryLoading"
            :max-height="520"
            @changed="loadGapUpLibrary"
          />
        </el-tab-pane>
      </el-tabs>
      <span slot="footer">
        <el-button @click="recordLibraryVisible = false">关闭</el-button>
      </span>
    </el-dialog>
    <el-dialog title="过滤配置" :visible.sync="filterConfigVisible" width="560px" append-to-body>
      <p class="tip">
        短 / 中 / 长各填三个分界值（百分数），生成「&lt;a、a~b、b~c」三档（默认 5 / 10 / 15），供对应下拉多选；空选表示不过滤。
      </p>
      <div v-for="item in rangeFields" :key="item.key" class="band-row">
        <span class="band-idx">{{ item.label }}</span>
        <el-input v-model="rangeDraft[item.key].a" placeholder="a" size="mini" style="width: 80px" />
        <span class="band-sep">/</span>
        <el-input v-model="rangeDraft[item.key].b" placeholder="b" size="mini" style="width: 80px" />
        <span class="band-sep">/</span>
        <el-input v-model="rangeDraft[item.key].c" placeholder="c" size="mini" style="width: 80px" />
        <span class="unit">%</span>
      </div>
      <p class="tip" style="margin-top: 16px">
        距年高涨幅：勾选后保留「(年高-收盘)/收盘×100」≤ 该阈值的票（默认 15）。
      </p>
      <div class="band-row">
        <span class="band-idx">距年高≤</span>
        <el-input v-model="rangeDraft.distToYearHighMax" placeholder="15" size="mini" style="width: 120px" />
        <span class="unit">%</span>
      </div>
      <p class="tip" style="margin-top: 16px">
        综合分（竞价TOP盘前初筛：不计二/三板绝杀，昨竞实换=当日）：勾选后保留 ≥ 该阈值（默认 45）。
      </p>
      <div class="band-row">
        <span class="band-idx">综合分≥</span>
        <el-input v-model="rangeDraft.preScreenScoreMin" placeholder="45" size="mini" style="width: 120px" />
      </div>
      <p class="tip" style="margin-top: 16px">
        流通市值：勾选后保留流通市值 ≤ 该阈值的票（亿，默认 70）。
      </p>
      <div class="band-row">
        <span class="band-idx">市值≤</span>
        <el-input v-model="rangeDraft.marketCapMax" placeholder="70" size="mini" style="width: 120px" />
        <span class="unit">亿</span>
      </div>
      <div slot="footer">
        <el-button size="small" @click="resetFilterConfig">恢复默认</el-button>
        <el-button size="small" @click="filterConfigVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="saveFilterConfig">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { BREAKPOINTS } from '@/utils/device';
import stockApi from '@/api/stockApi';
import { filterLadderStocks } from '@/utils/limitUpFilters';
import {
  RANGE_FIELDS,
  loadRangeConfig,
  saveRangeConfig,
  cloneRangeConfig,
  defaultRangeConfig,
  cutpointsToOptions,
  parsePct
} from '@/utils/hfbChipFilters';
import RecordLibraryTable from '@/components/RecordLibraryTable.vue';
import GapUpRecordLibraryTable from '@/components/GapUpRecordLibraryTable.vue';
import {
  MARKET_NODES,
  DRAGON_NODES,
  INDEX_TREND_OPTIONS,
  splitNodeTokens,
  parseNodeStatusForEdit,
  formatNodeStatus,
  hasDragonNode,
  getNodeClass,
  getNodeKind,
  buildColoredStars
} from '@/utils/reviewNodes';

const CART_OWNER_LABELS = {
  1: '师父',
  2: '小鱼'
};

export default {
  name: 'ReviewNotes',
  components: { RecordLibraryTable, GapUpRecordLibraryTable },
  data() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 14);
    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const rangeConfig = loadRangeConfig();
    return {
      startDate: fmt(start),
      endDate: fmt(end),
      pageLoading: false,
      queryLoading: false,
      syncLoading: false,
      ladderLoading: false,
      saveNoteLoading: false,
      nodeFactorEditFields: [
        { key: 'limitUpCount', label: '涨停家数', precision: 0 },
        { key: 'limitDownCount', label: '跌停家数', precision: 0 },
        { key: 'firstBoardCount', label: '首板数', precision: 0 },
        { key: 'secondBoardCount', label: '二板数', precision: 0 },
        { key: 'thirdBoardCount', label: '三板数', precision: 0 },
        { key: 'highBoardCount', label: '高度板数', precision: 0 },
        { key: 'secondPromoteRate', label: '二板晋级%', precision: 2 },
        { key: 'thirdPromoteRate', label: '三板晋级%', precision: 2 },
        { key: 'highPromoteRate', label: '高度晋级%', precision: 2 },
        { key: 'breakLimitRate', label: '破板率%', precision: 2 },
        { key: 'yestLimitPerf', label: '昨涨停表现%', precision: 2 },
        { key: 'yestBoardPerf', label: '昨连板表现%', precision: 2 },
        { key: 'yestBreakPerf', label: '昨破板表现%', precision: 2 }
      ],
      hasQueried: false,
      isMobile: false,
      mobileReviewExpanded: false,
      activeChartTab: 'temp',
      series: {
        tradeDates: [],
        temperature: [],
        boardHeight: [],
        boardHeightDisplay: [],
        boardHeightSecond: [],
        boardHeightSecondDisplay: [],
        reviewNodeHeight: [],
        indexTrend: [],
        marketVolume: [],
        ycln: [],
        marketIndex: [],
        marketIndexDisplay: [],
        greedyNodeScore: [],
        greedyNodeHit: [],
        greedyNodeHits: [],
        greedyNodeWeights: {},
        greedyNodeScheme: '',
        greedyNodeThreshold: 7,
        greedyNodeCatalog: [],
        notes: []
      },
      selectedTradeDate: '',
      selectedDateIndex: -1,
      selectedThemeCode: '',
      ladderThemes: [],
      ladderStocks: [],
      cartStocks: [],
      cartOwner: '1',
      cartReviewThought: '',
      cartSavedState: '',
      cartDialogVisible: false,
      saveCartLoading: false,
      onlyMainBoard: false,
      filterMarketValue: false,
      filterMa250: false,
      filterDistToYearHigh: false,
      filterPreScreenScore: false,
      rangeFields: RANGE_FIELDS,
      rangeConfig,
      rangeDraft: cloneRangeConfig(rangeConfig),
      filterConfigVisible: false,
      filterShortChipBands: [],
      filterMidChipBands: [],
      filterLongChipBands: [],
      recordLibraryVisible: false,
      recordLibraryTab: 'dragon',
      recordLibraryLoading: false,
      recordLibraryList: [],
      recordLibraryEditingId: '',
      recordLibraryScoreVersion: 'V5',
      gapUpLibraryList: [],
      gapUpLibraryLoading: false,
      ladderContextMenu: {
        visible: false,
        x: 0,
        y: 0,
        stock: null
      },
      ladderLongPressTimer: null,
      ladderLongPressTriggered: false,
      chart: null,
      noteDialogVisible: false,
      mobileDrawerVisible: false,
      editingNote: null,
      saveTimers: {}
    };
  },
  computed: {
    inputSize() {
      return this.isMobile ? 'mini' : 'small';
    },
    editingGreedyNodeDetail() {
      const hits = this.editingNote?.greedyNodeHits;
      return this.formatGreedyNodeHitLabels(hits);
    },
    greedyNodeSchemeTitle() {
      const scheme = this.series.greedyNodeScheme || 'V5F';
      const thr = this.series.greedyNodeThreshold ?? 7;
      const n = (this.series.greedyNodeCatalog || []).length;
      return `${scheme} · 阈值≥${thr} · ${n}节点`;
    },
    greedyNodeCatalogMap() {
      const map = {};
      for (const n of this.series.greedyNodeCatalog || []) {
        map[n.id] = n;
      }
      return map;
    },
    filteredLadderStocks() {
      let list = filterLadderStocks(this.ladderStocks, {
        onlyMainBoard: this.onlyMainBoard,
        filterMarketValue: this.filterMarketValue,
        marketCapMaxYi: this.marketCapMaxValue,
        filterMa250: this.filterMa250,
        filterDistToYearHigh: this.filterDistToYearHigh,
        distToYearHighMaxValue: this.distToYearHighMaxValue,
        filterPreScreenScore: this.filterPreScreenScore,
        preScreenScoreMinValue: this.preScreenScoreMinValue,
        filterMidChipBands: this.filterMidChipBands,
        filterShortChipBands: this.filterShortChipBands,
        filterLongChipBands: this.filterLongChipBands,
        shortChipOptions: this.shortChipOptions,
        midChipOptions: this.midChipOptions,
        longChipOptions: this.longChipOptions
      });
      const themeKey = String(this.selectedThemeCode || '').trim();
      if (themeKey) {
        list = list.filter(
          (s) =>
            String(s.themeCode || '').trim() === themeKey ||
            String(s.themeName || '').trim() === themeKey
        );
      }
      return list;
    },
    distToYearHighMaxDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.distToYearHighMax);
      return Number.isFinite(n) ? n : 15;
    },
    distToYearHighMaxValue() {
      const n = Number(this.rangeConfig && this.rangeConfig.distToYearHighMax);
      return Number.isFinite(n) ? n : 15;
    },
    marketCapMaxDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.marketCapMax);
      return Number.isFinite(n) ? n : 70;
    },
    marketCapMaxValue() {
      return this.marketCapMaxDisplay;
    },
    preScreenScoreMinDisplay() {
      const n = Number(this.rangeConfig && this.rangeConfig.preScreenScoreMin);
      return Number.isFinite(n) ? n : 45;
    },
    preScreenScoreMinValue() {
      const n = Number(this.rangeConfig && this.rangeConfig.preScreenScoreMin);
      return Number.isFinite(n) ? n : 45;
    },
    shortChipOptions() {
      return cutpointsToOptions(this.rangeConfig.short);
    },
    midChipOptions() {
      return cutpointsToOptions(this.rangeConfig.mid);
    },
    longChipOptions() {
      return cutpointsToOptions(this.rangeConfig.long);
    },
    ladderGroups() {
      const map = new Map();
      for (const s of this.filteredLadderStocks) {
        const b = Number(s.boards) || 0;
        if (!map.has(b)) map.set(b, []);
        map.get(b).push(s);
      }
      return Array.from(map.entries())
        .sort((a, b) => b[0] - a[0])
        .map(([boards, stocks]) => ({
          boards,
          stocks: stocks.sort((a, b) => String(a.limitUpTime).localeCompare(String(b.limitUpTime))),
          nodeStars: this.getBoardNodeStars(boards)
        }));
    },
    cartLadderGroups() {
      const map = new Map();
      for (const s of this.cartStocks) {
        const b = Number(s.boards) || 0;
        if (!map.has(b)) map.set(b, []);
        map.get(b).push(s);
      }
      return Array.from(map.entries())
        .sort((a, b) => b[0] - a[0])
        .map(([boards, stocks]) => ({
          boards,
          stocks,
          nodeStars: this.getBoardNodeStars(boards)
        }));
    },
    canPrevDay() {
      return this.selectedDateIndex > 0;
    },
    canNextDay() {
      return this.selectedDateIndex >= 0 && this.selectedDateIndex < this.series.tradeDates.length - 1;
    },
    cartCount() {
      return this.cartStocks.length;
    },
    cartDirty() {
      return this.serializeCartState() !== this.cartSavedState;
    },
    hasCartDraft() {
      return this.cartCount > 0 || String(this.cartReviewThought || '').trim();
    },
    cartDisplayNodes() {
      const idx = this.selectedDateIndex;
      if (idx < 0) return [];
      return splitNodeTokens(this.series.notes[idx]?.nodeStatus);
    },
    cartColoredNodeStars() {
      return buildColoredStars(this.cartDisplayNodes);
    },
    showPreFirstBoardSection() {
      return hasDragonNode(this.cartDisplayNodes);
    },
    cartPreFirstBoardStocks() {
      const idx = this.selectedDateIndex;
      if (idx < 0) return [];
      const key = this.cartOwner === '2' ? 'preFirstBoardStocks2' : 'preFirstBoardStocks1';
      const list = this.series.notes[idx]?.[key];
      return Array.isArray(list) ? list : [];
    },
    marketNodeOptions() {
      return MARKET_NODES;
    },
    dragonNodeOptions() {
      return DRAGON_NODES;
    },
    indexTrendOptions() {
      return INDEX_TREND_OPTIONS;
    },
    reviewThoughtHint() {
      const label = CART_OWNER_LABELS[this.cartOwner];
      if (this.cartOwner === '1') return '（师父 · 复盘备注1）';
      if (this.cartOwner === '2') return '（小鱼 · 复盘备注2）';
      return label ? `（${label}）` : '';
    },
    reviewThoughtPlaceholder() {
      const label = CART_OWNER_LABELS[this.cartOwner];
      return label ? `请输入${label}的复盘思路…` : '请输入复盘思路…';
    },
    cartDialogTitle() {
      const label = CART_OWNER_LABELS[this.cartOwner];
      const suffix = this.cartReadOnly ? '（只读）' : '';
      return label ? `复盘笔记 ${label}${suffix}` : `复盘笔记${suffix}`;
    },
    loggedInReviewOwner() {
      return this.defaultCartOwner();
    },
    cartReadOnly() {
      return this.cartOwner !== this.loggedInReviewOwner;
    },
    chartTabs() {
      return [
        { key: 'temp', label: '温度' },
        { key: 'height', label: '连板' },
        { key: 'volume', label: '量能' },
        { key: 'index', label: '指数' }
      ];
    },
    mobileSelectedMetricText() {
      const idx = this.selectedDateIndex;
      if (idx < 0) return '';
      const tab = this.activeChartTab;
      const map = {
        temp: () => `温度 ${this.fmtNum(this.series.temperature[idx])}`,
        height: () => `连板 ${this.fmtNum(this.series.boardHeightDisplay[idx])}`,
        volume: () => {
          const pct = this.fmtPct(this.series.marketVolume[idx]);
          const yi = this.series.ycln?.[idx];
          const extra = yi != null && Number.isFinite(Number(yi)) ? ` / ${Number(yi)}亿` : '';
          return `量能 ${pct}${extra}`;
        },
        index: () => `指数 ${this.fmtPct(this.series.marketIndexDisplay[idx])}`
      };
      return (map[tab] || map.temp)();
    },
    tableRows() {
      const s = this.series;
      return [
        {
          key: 'height',
          label: '高度',
          getDisplay: (idx) => {
            const n = s.notes[idx] || {};
            const h = s.boardHeightDisplay[idx];
            const second = s.boardHeightSecondDisplay[idx];
            const label = n.heightLabel || '';
            if (label) {
              return second != null ? `${label}\n次高${second}板` : label;
            }
            if (h == null) return '-';
            const main = `${h}板`;
            return second != null ? `${main}\n次高${second}板` : main;
          }
        },
        {
          key: 'reviewNodeHeight',
          label: '复盘节点高度',
          redBold: true,
          getDisplay: (idx) => {
            const v = String(s.reviewNodeHeight[idx] || '').trim();
            return v || '-';
          }
        },
        {
          key: 'indexTrend',
          label: '指数方向',
          getDisplay: (idx) => {
            const v = String(s.indexTrend[idx] || '').trim();
            return v || '-';
          }
        },
        { key: 'nodeStatus', label: '节点', getDisplay: (idx) => (s.notes[idx]?.nodeStatus || '-') },
        {
          key: 'greedyNodeScore',
          label: '节点分',
          getDisplay: (idx) => {
            const v = s.greedyNodeScore[idx];
            if (v == null || v === '') return '-';
            const hit = !!s.greedyNodeHit[idx];
            return hit ? `${v} ⬡` : String(v);
          }
        },
        {
          key: 'greedyNodeHits',
          label: '详细节点',
          showGreedyNodeHelp: true,
          getDisplay: (idx) => this.formatGreedyNodeHitsDisplay(idx)
        },
        { key: 'emotionVolumeNote', label: '情绪票爆量', getDisplay: (idx) => (s.notes[idx]?.emotionVolumeNote || '-') },
        { key: 'emotionNodeNote', label: '情绪票节点', getDisplay: (idx) => (s.notes[idx]?.emotionNodeNote || '-') },
        {
          key: 'reviewNote1',
          label: '复盘备注1',
          getDisplay: (idx) => this.ellipsis(s.notes[idx]?.reviewNote1),
          opensCart: true,
          cartOwner: '1'
        },
        {
          key: 'reviewNote2',
          label: '复盘备注2',
          getDisplay: (idx) => this.ellipsis(s.notes[idx]?.reviewNote2),
          opensCart: true,
          cartOwner: '2'
        }
      ];
    }
  },
  mounted() {
    this.cartOwner = this.defaultCartOwner();
    this.checkScreenSize();
    window.addEventListener('resize', this.onResize);
    document.addEventListener('click', this.closeLadderContextMenu);
    this.bootstrapFromRoute();
  },
  activated() {
    this.applyRouteQuery();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('click', this.closeLadderContextMenu);
    this.clearLadderLongPress();
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= BREAKPOINTS.MOBILE;
    },
    onResize() {
      this.checkScreenSize();
      if (this.chart) this.chart.resize();
      if (this.hasQueried) this.renderChart();
    },
    fmtNum(v) {
      return v == null ? '-' : v;
    },
    fmtPct(v) {
      if (v == null || v === '') return '-';
      const num = Number(v);
      if (!Number.isFinite(num)) return '-';
      const rounded = Math.round(num * 100) / 100;
      return `${rounded}%`;
    },
    mobileCardNotePreview(idx) {
      const n = this.series.notes[idx] || {};
      const text = n.heightLabel || n.nodeStatus || n.reviewNote1 || '';
      if (!text) return '';
      return text.length > 28 ? `${text.slice(0, 28)}…` : text;
    },
    ellipsis(text) {
      const s = String(text || '').trim();
      if (!s) return '-';
      return s.length > 12 ? `${s.slice(0, 12)}…` : s;
    },
    formatShortDate(td) {
      const p = String(td || '').split('-');
      return p.length === 3 ? `${Number(p[1])}/${Number(p[2])}` : td;
    },
    formatLimitTime(t) {
      if (!t) return '';
      const m = String(t).match(/(\d{1,2}:\d{2})/);
      return m ? m[1] : t.slice(0, 5);
    },
    formatPreScreenScore(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      return n.toFixed(1);
    },
    async handleQuery() {
      this.queryLoading = true;
      try {
        const res = await stockApi.fetchReviewSeries({ startDate: this.startDate, endDate: this.endDate });
        if (!res?.ok) throw new Error(res?.message || '查询失败');
        this.series = {
          tradeDates: res.tradeDates || [],
          temperature: res.temperature || [],
          boardHeight: res.boardHeight || [],
          boardHeightDisplay: res.boardHeightDisplay || [],
          boardHeightSecond: res.boardHeightSecond || [],
          boardHeightSecondDisplay: res.boardHeightSecondDisplay || [],
          reviewNodeHeight: res.reviewNodeHeight || [],
          indexTrend: res.indexTrend || [],
          marketVolume: res.marketVolume || [],
          ycln: res.ycln || [],
          marketIndex: res.marketIndex || [],
          marketIndexDisplay: res.marketIndexDisplay || res.marketIndex || [],
          greedyNodeScore: res.greedyNodeScore || [],
          greedyNodeHit: res.greedyNodeHit || [],
          greedyNodeHits: res.greedyNodeHits || [],
          greedyNodeWeights: res.greedyNodeWeights || {},
          greedyNodeScheme: res.greedyNodeScheme || '',
          greedyNodeThreshold: res.greedyNodeThreshold ?? 7,
          greedyNodeCatalog: res.greedyNodeCatalog || [],
          notes: res.notes || []
        };
        this.hasQueried = true;
        const lastIdx = this.series.tradeDates.length - 1;
        if (lastIdx >= 0) {
          this.selectDate(this.series.tradeDates[lastIdx], lastIdx, false);
        }
        this.$nextTick(() => {
          this.renderChart();
          return this.applyRouteQuery();
        });
      } catch (e) {
        this.$message.error(e.message || '查询失败');
      } finally {
        this.queryLoading = false;
      }
    },
    async handleSyncRange() {
      this.syncLoading = true;
      try {
        const res = await stockApi.syncReviewRange({ startDate: this.startDate, endDate: this.endDate });
        const synced = res?.syncedDays ?? 0;
        const failed = res?.failedDays ?? 0;
        const limitUp = res?.limitUpCount ?? 0;
        this.$message.success(
          `区间同步完成：${synced} 天，涨停 ${limitUp} 条${failed ? `，失败 ${failed} 天` : ''}`
        );
        await this.handleQuery();
      } catch (e) {
        this.$message.error(e.message || '区间同步失败');
      } finally {
        this.syncLoading = false;
      }
    },
    async selectDate(td, idx, loadLadder = true) {
      if (this.cartDirty && this.hasCartDraft && td !== this.selectedTradeDate) {
        try {
          await this.$confirm('复盘购物车有未保存的改动，切换日期将丢弃。是否继续？', '提示', {
            confirmButtonText: '继续切换',
            cancelButtonText: '取消',
            type: 'warning'
          });
        } catch {
          return;
        }
      }
      this.selectedTradeDate = td;
      this.selectedDateIndex = idx;
      this.loadCartFromNote(idx);
      if (loadLadder) this.loadLadder();
      this.renderChart();
      this.highlightChartIndex(idx);
    },
    shiftDay(delta) {
      const next = this.selectedDateIndex + delta;
      if (next < 0 || next >= this.series.tradeDates.length) return;
      this.selectDate(this.series.tradeDates[next], next);
    },
    async loadLadder() {
      if (!this.selectedTradeDate) return;
      this.ladderLoading = true;
      try {
        // 始终拉全量天梯，题材在前端勾选/去勾选过滤
        const res = await stockApi.fetchReviewLadder({
          tradeDate: this.selectedTradeDate
        });
        this.ladderThemes = res.themes || [];
        this.ladderStocks = res.stocks || [];
        const keys = new Set(this.ladderThemes.map((t) => this.themeKey(t)).filter(Boolean));
        if (this.selectedThemeCode && !keys.has(String(this.selectedThemeCode).trim())) {
          this.selectedThemeCode = '';
        }
      } catch (e) {
        this.$message.error('天梯加载失败');
      } finally {
        this.ladderLoading = false;
      }
    },
    themeKey(theme) {
      return String(theme?.code || theme?.name || '').trim();
    },
    isThemeSelected(theme) {
      const key = this.themeKey(theme);
      return !!key && String(this.selectedThemeCode || '').trim() === key;
    },
    selectTheme(theme) {
      const key = this.themeKey(theme);
      if (!key) return;
      // 再次点击同一题材 → 去勾选
      this.selectedThemeCode = this.isThemeSelected(theme) ? '' : key;
    },
    themeChipClass(theme) {
      if (this.isThemeSelected(theme)) return '';
      const idx = this.ladderThemes.indexOf(theme);
      if (idx === 0) return 'theme-chip--hot';
      if (idx === 1) return 'theme-chip--warm';
      return '';
    },
    onChartTabChange() {
      this.$nextTick(() => this.renderChart());
    },
    setChartTab(key) {
      if (this.activeChartTab === key) return;
      this.activeChartTab = key;
      this.onChartTabChange();
    },
    buildChartOption() {
      const dates = this.series.tradeDates.map((d) => this.formatShortDate(d));
      const notes = this.series.notes || [];
      const heightLabels = notes.map((n, i) => {
        const parts = [];
        if (n.heightLabel) parts.push(n.heightLabel);
        else if (n.chartAnnotations) parts.push(n.chartAnnotations);
        else if (this.series.boardHeightDisplay[i] != null) {
          parts.push(`${this.series.boardHeightDisplay[i]}板`);
        }
        return parts.join('\n');
      });

      const chartColors = {
        temp: '#5B8FF9',
        height: '#F6BD16',
        heightSecond: '#722ED1',
        nodeHeight: '#E53935',
        volume: '#5AD8A6',
        index: '#E86452'
      };

      const selectedMark = this.selectedDateIndex >= 0
        ? {
            symbol: ['none', 'none'],
            label: { show: false },
            lineStyle: { color: '#909399', type: 'dashed', width: 1, opacity: 0.8 },
            data: [{ xAxis: this.selectedDateIndex }]
          }
        : undefined;

      const grids = this.isMobile
        ? [{ left: 58, right: 32, top: 44, height: '68%' }]
        : [
            { left: 64, right: 32, top: 44, height: 108 },
            { left: 64, right: 32, top: 188, height: 132 },
            { left: 64, right: 32, top: 356, height: 108 },
            { left: 64, right: 32, bottom: 52, height: 84 }
          ];

      const axisFont = 12;
      const baseAxis = {
        axisLine: { lineStyle: { color: '#E4E7ED' } },
        axisTick: { show: false },
        axisLabel: { color: '#606266', fontSize: axisFont }
      };

      const xAxis = (gridIndex, showLabel = false) => ({
        type: 'category',
        gridIndex,
        data: dates,
        boundaryGap: true,
        ...baseAxis,
        axisLabel: {
          ...baseAxis.axisLabel,
          show: showLabel,
          fontSize: axisFont,
          margin: showLabel ? 10 : 8
        },
        axisPointer: { show: true }
      });

      const formatPointValue = (value, withPercent = false) => {
        if (value == null || value === '') return '';
        const num = Number(value);
        if (!Number.isFinite(num)) return '';
        if (withPercent) {
          const rounded = Math.round(num * 100) / 100;
          return `${rounded}%`;
        }
        return Number.isInteger(num) ? String(num) : String(Math.round(num * 10) / 10);
      };

      const labelPositionAbovePoint = (point, params, _dom, _rect, size) => {
        const total = dates.length;
        const isLast = total > 0 && params.dataIndex >= total - 1;
        const isNearEnd = total > 0 && params.dataIndex >= total - 2;
        const w = size.contentSize[0] || 0;
        const h = size.contentSize[1] || 0;
        let x = point[0] - w / 2;
        if (isLast) x = point[0] - w - 10;
        else if (isNearEnd) x = point[0] - w * 0.75;
        const y = point[1] - h - 12;
        return [x, y];
      };

      const pointValueLabel = (withPercent, accentColor, mobileOnlySelected = false) => ({
        show: (params) => {
          if (formatPointValue(params.value, withPercent) === '') return false;
          if (mobileOnlySelected && params.dataIndex !== this.selectedDateIndex) return false;
          return true;
        },
        clip: false,
        position: labelPositionAbovePoint,
        distance: 10,
        fontSize: 11,
        fontWeight: 600,
        color: '#303133',
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderColor: accentColor,
        borderWidth: 1,
        borderRadius: 3,
        padding: [3, 6],
        formatter: (p) => formatPointValue(p.value, withPercent)
      });

      const thresholdPointValueLabel = (
        withPercent,
        accentColor,
        { posThreshold, negThreshold, mobileOnlySelected = false } = {}
      ) => ({
        show: (params) => {
          if (formatPointValue(params.value, withPercent) === '') return false;
          if (mobileOnlySelected && params.dataIndex !== this.selectedDateIndex) return false;
          return true;
        },
        clip: false,
        position: labelPositionAbovePoint,
        distance: 10,
        padding: 0,
        backgroundColor: 'transparent',
        borderWidth: 0,
        rich: {
          red: {
            color: '#FFFFFF',
            fontWeight: 'bold',
            fontSize: 11,
            backgroundColor: '#E53935',
            padding: [4, 7],
            borderRadius: 3
          },
          green: {
            color: '#FFFFFF',
            fontWeight: 'bold',
            fontSize: 11,
            backgroundColor: '#43A047',
            padding: [4, 7],
            borderRadius: 3
          },
          normal: {
            color: '#303133',
            fontWeight: 600,
            fontSize: 11,
            backgroundColor: 'rgba(255,255,255,0.96)',
            borderColor: accentColor,
            borderWidth: 1,
            padding: [3, 6],
            borderRadius: 3
          }
        },
        formatter: (p) => {
          const text = formatPointValue(p.value, withPercent);
          if (!text) return '';
          const v = Number(p.value);
          if (Number.isFinite(v)) {
            if (posThreshold != null && v > posThreshold) return `{red|${text}}`;
            if (negThreshold != null && v < negThreshold) return `{green|${text}}`;
          }
          return `{normal|${text}}`;
        }
      });

      const yAxis = (gridIndex, extra = {}) => ({
        type: 'value',
        gridIndex,
        scale: true,
        ...baseAxis,
        splitLine: { lineStyle: { color: '#F0F2F5', type: 'solid' } },
        ...extra
      });

      const reviewNodeHeightTexts = this.series.reviewNodeHeight || [];
      const reviewNodeHeightSeries = {
        name: '复盘节点高度',
        type: 'scatter',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: reviewNodeHeightTexts.map((raw, idx) => {
          const text = String(raw || '').trim();
          if (!text) return null;
          return [idx, 0];
        }),
        symbolSize: 0,
        tooltip: { show: false },
        z: 10,
        label: {
          show: true,
          formatter: (params) => String(reviewNodeHeightTexts[params.dataIndex] || '').trim(),
          color: chartColors.nodeHeight,
          fontWeight: 'bold',
          fontSize: 11,
          backgroundColor: 'rgba(255,255,255,0.96)',
          borderColor: chartColors.nodeHeight,
          borderWidth: 1,
          borderRadius: 4,
          padding: [4, 7],
          position: 'bottom',
          distance: 4
        },
        labelLayout: { moveOverlap: 'shiftY' }
      };

      const chartTitles = this.isMobile
        ? []
        : [
            { text: '温度', left: 20, top: 16, textStyle: { fontSize: 14, fontWeight: 600, color: chartColors.temp } },
            { text: '连板高度', left: 20, top: 160, textStyle: { fontSize: 14, fontWeight: 600, color: chartColors.height } },
            { text: '量能', left: 20, top: 328, textStyle: { fontSize: 14, fontWeight: 600, color: chartColors.volume } },
            { text: '指数', left: 20, top: 468, textStyle: { fontSize: 14, fontWeight: 600, color: chartColors.index } }
          ];

      const tooltipValue = (name, value) => {
        if (value == null || value === '') return '-';
        const num = Number(value);
        if (!Number.isFinite(num)) return '-';
        if (name === '量能' || name === '指数') {
          const rounded = Math.round(num * 100) / 100;
          return `${rounded}%`;
        }
        if (name === '连板高度' || name === '次高连板') {
          return `${num}板`;
        }
        return Number.isInteger(num) ? String(num) : String(Math.round(num * 10) / 10);
      };

      const axisTooltipFormatter = (params) => {
        const items = Array.isArray(params) ? params : [params];
        if (!items.length) return '';
        const date = items[0].axisValue;
        const order = ['指数', '量能', '温度', '连板高度', '次高连板'];
        const byName = {};
        items.forEach((p) => {
          if (p?.seriesName) byName[p.seriesName] = p;
        });
        const lines = [`<span style="font-weight:600">${date}</span>`];
        order.forEach((name) => {
          const p = byName[name];
          if (!p) return;
          let text = tooltipValue(name, p.value);
          if (name === '量能') {
            const yi = this.series.ycln?.[p.dataIndex];
            if (yi != null && Number.isFinite(Number(yi))) {
              text += ` / 成交额 ${Number(yi)}亿`;
            }
          }
          lines.push(`${p.marker}${name}&nbsp;&nbsp;${text}`);
        });
        return lines.join('<br/>');
      };

      const lineSeries = (gridIndex, name, data, color, extra = {}) => ({
        name,
        type: 'line',
        xAxisIndex: gridIndex,
        yAxisIndex: gridIndex,
        data,
        smooth: false,
        symbol: 'circle',
        symbolSize: 7,
        lineStyle: { width: 2, color },
        itemStyle: { color, borderWidth: 2, borderColor: '#fff' },
        connectNulls: false,
        markLine: selectedMark,
        ...extra
      });

      const heightAnnotationLabel = (mobileOnlySelected = false) => ({
        show: (params) => {
          if (!heightLabels[params.dataIndex]) return false;
          if (mobileOnlySelected && params.dataIndex !== this.selectedDateIndex) return false;
          return true;
        },
        clip: false,
        position: labelPositionAbovePoint,
        fontSize: 12,
        fontWeight: 600,
        color: '#303133',
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderColor: chartColors.height,
        borderWidth: 1,
        borderRadius: 4,
        padding: [4, 7],
        lineHeight: 16,
        overflow: 'break',
        formatter: (p) => heightLabels[p.dataIndex] || ''
      });

      const secondHeightLabel = (mobileOnlySelected = false) => ({
        show: (params) => {
          if (formatPointValue(params.value, false) === '') return false;
          if (mobileOnlySelected && params.dataIndex !== this.selectedDateIndex) return false;
          return true;
        },
        clip: false,
        position: labelPositionAbovePoint,
        distance: 10,
        fontSize: 12,
        fontWeight: 600,
        color: '#303133',
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderColor: chartColors.heightSecond,
        borderWidth: 1,
        borderRadius: 4,
        padding: [4, 7],
        formatter: (p) => {
          const v = formatPointValue(p.value, false);
          return v ? `次高${v}板` : '';
        }
      });

      if (this.isMobile) {
        const tabMap = {
          temp: {
            name: '温度',
            data: this.series.temperature,
            color: chartColors.temp,
            label: pointValueLabel(false, chartColors.temp, true)
          },
          height: {
            name: '连板高度',
            data: this.series.boardHeightDisplay,
            color: chartColors.height,
            label: heightAnnotationLabel(true)
          },
          volume: {
            name: '量能',
            data: this.series.marketVolume,
            color: chartColors.volume,
            label: thresholdPointValueLabel(true, chartColors.volume, {
              posThreshold: 4.8,
              negThreshold: -4.8,
              mobileOnlySelected: true
            })
          },
          index: {
            name: '指数',
            data: this.series.marketIndexDisplay,
            color: chartColors.index,
            label: thresholdPointValueLabel(true, chartColors.index, {
              posThreshold: 0.48,
              negThreshold: -0.48,
              mobileOnlySelected: true
            })
          }
        };
        const cur = tabMap[this.activeChartTab] || tabMap.temp;
        return {
          backgroundColor: 'transparent',
          title: [{ text: cur.name, left: 12, top: 6, textStyle: { fontSize: 13, fontWeight: 600, color: cur.color } }],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(255,255,255,0.98)',
            borderColor: '#EBEEF5',
            textStyle: { color: '#303133', fontSize: 12 },
            confine: true,
            formatter: this.activeChartTab === 'height' ? axisTooltipFormatter : (params) => {
              const items = Array.isArray(params) ? params : [params];
              if (!items.length) return '';
              const p = items[0];
              let val = p.seriesName === '量能' || p.seriesName === '指数'
                ? formatPointValue(p.value, true)
                : p.seriesName === '连板高度' || p.seriesName === '次高连板'
                  ? tooltipValue(p.seriesName, p.value)
                  : formatPointValue(p.value, false);
              if (p.seriesName === '量能') {
                const yi = this.series.ycln?.[p.dataIndex];
                if (yi != null && Number.isFinite(Number(yi))) {
                  val = `${val || '-'} / 成交额 ${Number(yi)}亿`;
                }
              }
              return `${p.axisValue}<br/>${p.marker}${p.seriesName}&nbsp;&nbsp;${val || '-'}`;
            }
          },
          grid: [{ left: 48, right: 20, top: 36, bottom: 48 }],
          xAxis: [xAxis(0, true)],
          yAxis: [yAxis(0)],
          dataZoom: [{ type: 'inside' }],
          series: this.activeChartTab === 'height'
            ? [
                lineSeries(0, '连板高度', this.series.boardHeightDisplay, chartColors.height, {
                  symbolSize: 6,
                  label: heightAnnotationLabel(true),
                  labelLayout: { moveOverlap: 'shiftY' }
                }),
                lineSeries(0, '次高连板', this.series.boardHeightSecondDisplay, chartColors.heightSecond, {
                  symbolSize: 6,
                  markLine: undefined,
                  lineStyle: { width: 2, color: chartColors.heightSecond, type: 'dashed' },
                  label: secondHeightLabel(true),
                  labelLayout: { moveOverlap: 'shiftY' }
                })
              ]
            : [
                lineSeries(0, cur.name, cur.data, cur.color, {
                  areaStyle: { color: cur.color, opacity: 0.12 },
                  symbolSize: 6,
                  label: cur.label
                })
              ]
        };
      }

      return {
        backgroundColor: 'transparent',
        title: chartTitles,
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255,255,255,0.96)',
          borderColor: '#EBEEF5',
          textStyle: { color: '#303133', fontSize: 13 },
          formatter: axisTooltipFormatter,
          axisPointer: { type: 'line', link: [{ xAxisIndex: 'all' }] }
        },
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        grid: grids,
        xAxis: [xAxis(0), xAxis(1), xAxis(2), xAxis(3, true)],
        yAxis: [yAxis(0), yAxis(1, { min: 0 }), yAxis(2), yAxis(3)],
        dataZoom: [
          { type: 'inside', xAxisIndex: [0, 1, 2, 3] },
          {
            type: 'slider',
            xAxisIndex: [0, 1, 2, 3],
            height: 20,
            bottom: 6,
            borderColor: '#EBEEF5',
            fillerColor: 'rgba(91,143,249,0.12)'
          }
        ],
        series: [
          lineSeries(3, '指数', this.series.marketIndexDisplay, chartColors.index, {
            label: thresholdPointValueLabel(true, chartColors.index, {
              posThreshold: 0.48,
              negThreshold: -0.48
            }),
            labelLayout: { moveOverlap: 'shiftY' }
          }),
          lineSeries(2, '量能', this.series.marketVolume, chartColors.volume, {
            label: thresholdPointValueLabel(true, chartColors.volume, {
              posThreshold: 4.8,
              negThreshold: -4.8
            }),
            labelLayout: { moveOverlap: 'shiftY' }
          }),
          lineSeries(0, '温度', this.series.temperature, chartColors.temp, {
            areaStyle: { color: chartColors.temp, opacity: 0.06 },
            label: pointValueLabel(false, chartColors.temp),
            labelLayout: { moveOverlap: 'shiftY' }
          }),
          lineSeries(1, '连板高度', this.series.boardHeightDisplay, chartColors.height, {
            label: heightAnnotationLabel(false),
            labelLayout: { moveOverlap: 'shiftY' }
          }),
          lineSeries(1, '次高连板', this.series.boardHeightSecondDisplay, chartColors.heightSecond, {
            markLine: undefined,
            lineStyle: { width: 2, color: chartColors.heightSecond, type: 'dashed' },
            label: secondHeightLabel(false),
            labelLayout: { moveOverlap: 'shiftY' }
          }),
          reviewNodeHeightSeries
        ]
      };
    },
    renderChart() {
      const el = this.$refs.chartRef;
      if (!el || !this.series.tradeDates.length) return;
      if (!this.chart) {
        this.chart = echarts.init(el);
        this.chart.on('click', (params) => {
          if (params.dataIndex >= 0) {
            this.selectDate(this.series.tradeDates[params.dataIndex], params.dataIndex);
          }
        });
      }
      this.chart.setOption(this.buildChartOption(), true);
      this.chart.resize();
      if (this.selectedDateIndex >= 0) this.highlightChartIndex(this.selectedDateIndex);
    },
    highlightChartIndex(idx) {
      if (!this.chart) return;
      this.chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: idx });
    },
    normalizeOptionalNumber(value) {
      if (value === '' || value === undefined || value === null) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    },
    buildEditNotePayload(note) {
      return {
        tradeDate: note.tradeDate,
        boardHeightOverride: this.normalizeOptionalNumber(note.boardHeightOverride),
        boardHeightSecondOverride: this.normalizeOptionalNumber(note.boardHeightSecondOverride),
        reviewNodeHeight: String(note.reviewNodeHeight || '').trim(),
        marketIndexOverride: this.normalizeOptionalNumber(note.marketIndexOverride),
        indexTrend: note.indexTrend || '',
        heightLabel: note.heightLabel || '',
        nodeStatus: formatNodeStatus(note.selectedNodes),
        emotionVolumeNote: note.emotionVolumeNote || '',
        emotionNodeNote: note.emotionNodeNote || '',
        chartAnnotations: note.chartAnnotations || ''
      };
    },
    currentUsername() {
      try {
        const raw = localStorage.getItem('userInfo');
        return raw ? (JSON.parse(raw).username || '') : '';
      } catch {
        return '';
      }
    },
    reviewNoteFieldKey() {
      return this.reviewNoteFieldKeyForOwner(this.cartOwner);
    },
    reviewNoteFieldKeyForOwner(owner) {
      return owner === '2' ? 'reviewNote2' : 'reviewNote1';
    },
    pickedStocksFieldKeyForOwner(owner) {
      return owner === '2' ? 'pickedStocks2' : 'pickedStocks1';
    },
    cartOwnerFromRowKey(rowKey) {
      return rowKey === 'reviewNote2' ? '2' : '1';
    },
    defaultCartOwner() {
      try {
        const raw = localStorage.getItem('userInfo');
        const info = raw ? JSON.parse(raw) : {};
        const configured = String(info.reviewOwner || '').trim();
        if (configured === '1' || configured === '2') return configured;
      } catch {
        // ignore
      }
      const u = this.currentUsername();
      if (u === 'hjx' || u === '小鱼') return '2';
      if (u === 'Chris' || u === '师父') return '1';
      return '1';
    },
    cartOwnerLabel(owner) {
      return CART_OWNER_LABELS[owner === '2' ? '2' : '1'] || '';
    },
    canEditCartOwner(owner) {
      return String(owner) === String(this.loggedInReviewOwner);
    },
    getNodeClass(node) {
      return getNodeClass(node);
    },
    getNodeTagClass(node) {
      const kind = getNodeKind(node);
      if (kind === 'dragon') return 'cart-node-tag--dragon';
      return 'cart-node-tag--market';
    },
    getNodeTokensForIndex(idx) {
      return splitNodeTokens(this.series.notes[idx]?.nodeStatus);
    },
    getNodesForFirstBoardDate(boards) {
      const fbIdx = this.getFirstBoardDateIndex(boards);
      if (fbIdx < 0) return [];
      const td = this.series.tradeDates[fbIdx];
      const note = (this.series.notes || []).find((n) => n.tradeDate === td)
        || this.series.notes[fbIdx]
        || {};
      return splitNodeTokens(note.nodeStatus);
    },
    getBoardNodeStars(boards) {
      return buildColoredStars(this.getNodesForFirstBoardDate(boards));
    },
    getFirstBoardDateIndex(boards) {
      const b = Number(boards) || 1;
      const idx = this.selectedDateIndex - b + 1;
      return idx >= 0 ? idx : -1;
    },
    getNodeStarCountForBoards(boards) {
      const fbIdx = this.getFirstBoardDateIndex(boards);
      if (fbIdx < 0) return 0;
      const td = this.series.tradeDates[fbIdx];
      const note = (this.series.notes || []).find((n) => n.tradeDate === td)
        || this.series.notes[fbIdx]
        || {};
      return splitNodeTokens(note.nodeStatus).length;
    },
    getStockNodeStars(stock) {
      return this.getBoardNodeStars(stock?.boards);
    },
    getReviewThoughtFromNote(note, owner = this.cartOwner) {
      const n = note || {};
      const key = this.reviewNoteFieldKeyForOwner(owner);
      return String(n[key] || '').trim();
    },
    getPickedStocksFromNote(note, owner = this.cartOwner) {
      const n = note || {};
      const key = this.pickedStocksFieldKeyForOwner(owner);
      const list = n[key];
      return Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
    },
    openNoteDialog(td) {
      const idx = this.series.tradeDates.indexOf(td);
      const base = idx >= 0 ? { ...(this.series.notes[idx] || {}) } : {};
      const displayHeight = idx >= 0 ? this.series.boardHeightDisplay[idx] : null;
      const displayIndex = idx >= 0 ? this.series.marketIndexDisplay[idx] : null;
      const displaySecondHeight = idx >= 0 ? this.series.boardHeightSecondDisplay[idx] : null;
      this.editingNote = {
        tradeDate: td,
        boardHeightOverride: base.boardHeightOverride != null ? base.boardHeightOverride : displayHeight,
        boardHeightSecondOverride:
          base.boardHeightSecondOverride != null ? base.boardHeightSecondOverride : displaySecondHeight,
        reviewNodeHeight:
          base.reviewNodeHeight != null && String(base.reviewNodeHeight).trim()
            ? String(base.reviewNodeHeight).trim()
            : String(idx >= 0 ? this.series.reviewNodeHeight[idx] || '' : '').trim(),
        marketIndexOverride: base.marketIndexOverride != null ? base.marketIndexOverride : displayIndex,
        heightLabel: base.heightLabel || '',
        indexTrend: base.indexTrend || (idx >= 0 ? this.series.indexTrend[idx] || '' : ''),
        selectedNodes: parseNodeStatusForEdit(base.nodeStatus),
        emotionVolumeNote: base.emotionVolumeNote || '',
        emotionNodeNote: base.emotionNodeNote || '',
        chartAnnotations: base.chartAnnotations || '',
        nodeFactors: this.emptyNodeFactors(),
        nodeFactorSource: '',
        greedyNodeScore: null,
        greedyNodeHit: false,
        greedyNodeHits: []
      };
      this.noteDialogVisible = true;
      this.loadEditingNodeFactors(td);
    },
    emptyNodeFactors() {
      const o = {};
      for (const item of this.nodeFactorEditFields) o[item.key] = null;
      return o;
    },
    applyNodeFactorDoc(doc) {
      if (!this.editingNote || !doc) return;
      const nf = this.emptyNodeFactors();
      for (const item of this.nodeFactorEditFields) {
        nf[item.key] = doc[item.key] != null ? Number(doc[item.key]) : null;
      }
      this.editingNote.nodeFactors = nf;
      this.editingNote.nodeFactorSource = doc.nodeFactorSource || '';
      this.editingNote.greedyNodeScore = doc.greedyNodeScore != null ? doc.greedyNodeScore : null;
      this.editingNote.greedyNodeHit = !!doc.greedyNodeHit;
      this.editingNote.greedyNodeHits = Array.isArray(doc.greedyNodeHits)
        ? doc.greedyNodeHits.map(String)
        : [];
    },
    formatGreedyNodeHitLabel(id) {
      const wm = this.series.greedyNodeWeights || {};
      return `${id}(+${wm[id] ?? '?'})`;
    },
    formatGreedyNodeHitLabels(hits) {
      if (!Array.isArray(hits) || !hits.length) return '无';
      return hits.map((id) => this.formatGreedyNodeHitLabel(id)).join('、');
    },
    getGreedyNodeMeta(id) {
      return this.greedyNodeCatalogMap[id] || null;
    },
    getGreedyNodeWeight(id) {
      const wm = this.series.greedyNodeWeights || {};
      if (wm[id] != null) return wm[id];
      const meta = this.getGreedyNodeMeta(id);
      return meta?.weight ?? '?';
    },
    getGreedyNodeFactor(id) {
      return this.getGreedyNodeMeta(id)?.factor || '—';
    },
    getGreedyNodeRule(id) {
      return this.getGreedyNodeMeta(id)?.rule || '暂无规则说明';
    },
    formatGreedyNodeHitsDisplay(idx) {
      const hits = this.getGreedyNodeHitsForIndex(idx);
      if (!hits.length) return '-';
      return this.formatGreedyNodeHitLabels(hits);
    },
    getGreedyNodeHitsForIndex(idx) {
      const hits = this.series.greedyNodeHits?.[idx];
      return Array.isArray(hits) ? hits : [];
    },
    async loadEditingNodeFactors(td) {
      try {
        const res = await stockApi.getMarketNodeFactors(td);
        if (res?.ok && res.doc) this.applyNodeFactorDoc(res.doc);
      } catch (e) {
        /* 无数据时保持空 */
      }
    },
    openMobileDrawer(td) {
      this.openNoteDialog(td);
      this.mobileDrawerVisible = true;
      this.noteDialogVisible = false;
    },
    async saveEditingNote() {
      if (!this.editingNote?.tradeDate) return;
      this.saveNoteLoading = true;
      try {
        const payload = this.buildEditNotePayload(this.editingNote);
        const idx = this.series.tradeDates.indexOf(payload.tradeDate);
        const res = await stockApi.saveReviewDayNote(payload);
        try {
          const factorRes = await stockApi.saveMarketNodeFactors({
            tradeDate: payload.tradeDate,
            fields: { ...(this.editingNote.nodeFactors || {}) }
          });
          if (factorRes?.ok && factorRes.doc) {
            this.applyNodeFactorDoc(factorRes.doc);
            if (idx >= 0) {
              this.$set(this.series.greedyNodeScore, idx, factorRes.doc.greedyNodeScore ?? null);
              this.$set(this.series.greedyNodeHit, idx, !!factorRes.doc.greedyNodeHit);
              this.$set(
                this.series.greedyNodeHits,
                idx,
                Array.isArray(factorRes.doc.greedyNodeHits)
                  ? factorRes.doc.greedyNodeHits.map(String)
                  : []
              );
            }
          }
        } catch (fe) {
          this.$message.warning('笔记已存，日因子保存失败');
        }
        const saved = res?.note || payload;
        if (idx >= 0) {
          this.$set(this.series.notes, idx, {
            ...(this.series.notes[idx] || { tradeDate: payload.tradeDate }),
            boardHeightOverride: saved.boardHeightOverride ?? payload.boardHeightOverride,
            boardHeightSecondOverride: saved.boardHeightSecondOverride ?? payload.boardHeightSecondOverride,
            reviewNodeHeight: saved.reviewNodeHeight ?? payload.reviewNodeHeight,
            marketIndexOverride: saved.marketIndexOverride ?? payload.marketIndexOverride,
            heightLabel: saved.heightLabel ?? payload.heightLabel,
            indexTrend: saved.indexTrend ?? payload.indexTrend,
            nodeStatus: saved.nodeStatus ?? payload.nodeStatus,
            emotionVolumeNote: saved.emotionVolumeNote ?? payload.emotionVolumeNote,
            emotionNodeNote: saved.emotionNodeNote ?? payload.emotionNodeNote,
            chartAnnotations: saved.chartAnnotations ?? payload.chartAnnotations
          });
          const heightOverride = saved.boardHeightOverride ?? payload.boardHeightOverride;
          this.$set(
            this.series.boardHeightDisplay,
            idx,
            heightOverride != null ? heightOverride : this.series.boardHeight[idx]
          );
          const secondOverride = saved.boardHeightSecondOverride ?? payload.boardHeightSecondOverride;
          this.$set(
            this.series.boardHeightSecondDisplay,
            idx,
            secondOverride != null ? secondOverride : this.series.boardHeightSecond[idx]
          );
          const nodeHeight = String(saved.reviewNodeHeight ?? payload.reviewNodeHeight ?? '').trim();
          this.$set(this.series.reviewNodeHeight, idx, nodeHeight);
          this.$set(this.series.indexTrend, idx, String(saved.indexTrend ?? payload.indexTrend ?? '').trim());
          const indexOverride = saved.marketIndexOverride ?? payload.marketIndexOverride;
          this.$set(
            this.series.marketIndexDisplay,
            idx,
            indexOverride != null ? indexOverride : this.series.marketIndex[idx]
          );
        }
        this.renderChart();
        this.noteDialogVisible = false;
        this.mobileDrawerVisible = false;
        this.$message.success('已保存');
      } catch (e) {
        this.$message.error('保存失败');
      } finally {
        this.saveNoteLoading = false;
      }
    },
    onFilterMa250Change(checked) {
      if (!checked) return;
      const has = this.ladderStocks.some((s) => s.ma250 != null);
      if (!has) {
        this.$message.warning('当前暂无 MA250（需先有足够日线历史，接口现算）');
      }
    },
    toggleFilterMa250() {
      const next = !this.filterMa250;
      this.filterMa250 = next;
      if (next) this.onFilterMa250Change(true);
    },
    toggleChipBand(kind, value) {
      const key =
        kind === 'short'
          ? 'filterShortChipBands'
          : kind === 'long'
            ? 'filterLongChipBands'
            : 'filterMidChipBands';
      const cur = [...(this[key] || [])];
      const i = cur.indexOf(value);
      if (i >= 0) cur.splice(i, 1);
      else cur.push(value);
      this[key] = cur;
    },
    openFilterConfig() {
      this.rangeDraft = cloneRangeConfig(this.rangeConfig);
      this.filterConfigVisible = true;
    },
    resetFilterConfig() {
      this.rangeDraft = defaultRangeConfig();
    },
    saveFilterConfig() {
      const next = cloneRangeConfig(this.rangeDraft);
      for (const f of RANGE_FIELDS) {
        const aPct = parsePct(next[f.key].a);
        const bPct = parsePct(next[f.key].b);
        const cPct = parsePct(next[f.key].c);
        if (aPct == null || bPct == null || cPct == null) {
          this.$message.warning(`${f.label}请填写三个分界值`);
          return;
        }
        if (!(aPct < bPct && bPct < cPct)) {
          this.$message.warning(`${f.label}须满足 a < b < c`);
          return;
        }
      }
      const distMax = Number(next.distToYearHighMax);
      if (!Number.isFinite(distMax)) {
        this.$message.warning('距年高阈值须为数字');
        return;
      }
      const preMin = Number(next.preScreenScoreMin);
      if (!Number.isFinite(preMin)) {
        this.$message.warning('综合分阈值须为数字');
        return;
      }
      const capMax = Number(next.marketCapMax);
      if (!Number.isFinite(capMax) || capMax <= 0) {
        this.$message.warning('流通市值上限须为正数');
        return;
      }
      next.distToYearHighMax = String(distMax);
      next.preScreenScoreMin = String(preMin);
      next.marketCapMax = String(capMax);
      this.rangeConfig = next;
      saveRangeConfig(next);
      const shortOk = new Set(this.shortChipOptions.map((o) => o.value));
      const midOk = new Set(this.midChipOptions.map((o) => o.value));
      const longOk = new Set(this.longChipOptions.map((o) => o.value));
      this.filterShortChipBands = this.filterShortChipBands.filter((v) => shortOk.has(v));
      this.filterMidChipBands = this.filterMidChipBands.filter((v) => midOk.has(v));
      this.filterLongChipBands = this.filterLongChipBands.filter((v) => longOk.has(v));
      this.filterConfigVisible = false;
      this.$message.success('过滤配置已保存');
    },
    toggleMobileReview() {
      this.mobileReviewExpanded = !this.mobileReviewExpanded;
    },
    stockCodeKey(stock) {
      return String(stock?.code || '').trim().split('.')[0];
    },
    sortCartStocks(list) {
      return [...(list || [])].sort((a, b) => {
        const diff = (Number(b.boards) || 0) - (Number(a.boards) || 0);
        if (diff !== 0) return diff;
        return String(a.limitUpTime || '').localeCompare(String(b.limitUpTime || ''));
      });
    },
    serializeCart(list) {
      const rows = (list || [])
        .map((s) => this.stockCodeKey(s))
        .filter(Boolean)
        .sort();
      return JSON.stringify(rows);
    },
    serializeCartState() {
      return JSON.stringify({
        owner: this.cartOwner,
        stocks: this.serializeCart(this.cartStocks),
        thought: String(this.cartReviewThought || '').trim()
      });
    },
    loadCartFromNote(idx) {
      const n = idx >= 0 ? (this.series.notes[idx] || {}) : {};
      this.cartStocks = this.sortCartStocks(this.getPickedStocksFromNote(n, this.cartOwner));
      this.cartReviewThought = this.getReviewThoughtFromNote(n, this.cartOwner);
      this.cartSavedState = this.serializeCartState();
    },
    async switchCartOwner(nextOwner) {
      if (nextOwner === this.cartOwner) return true;
      if (this.cartDirty) {
        try {
          await this.$confirm('当前购物车有未保存改动，切换将丢弃。是否继续？', '提示', {
            confirmButtonText: '继续切换',
            cancelButtonText: '取消',
            type: 'warning'
          });
        } catch {
          return false;
        }
      }
      this.cartOwner = nextOwner;
      this.loadCartFromNote(this.selectedDateIndex);
      return true;
    },
    isInCart(stock) {
      const code = this.stockCodeKey(stock);
      return this.cartStocks.some((s) => this.stockCodeKey(s) === code);
    },
    buildPickedStockPayload(stock) {
      return {
        code: this.stockCodeKey(stock),
        name: stock.name || '',
        boards: Number(stock.boards) || 1,
        themeName: stock.themeName || '',
        themeCode: stock.themeCode || '',
        limitUpTime: stock.limitUpTime || '',
        freeFloatMarketCap: stock.freeFloatMarketCap ?? null
      };
    },
    toggleCartStock(stock) {
      if (!this.selectedTradeDate) {
        this.$message.warning('请先选择日期');
        return;
      }
      if (this.cartReadOnly) {
        this.$message.warning(`只能编辑${this.cartOwnerLabel(this.loggedInReviewOwner)}的复盘笔记`);
        return;
      }
      if (this.isInCart(stock)) {
        this.removeFromCart(this.stockCodeKey(stock));
        return;
      }
      this.cartStocks = this.sortCartStocks([
        ...this.cartStocks,
        this.buildPickedStockPayload(stock)
      ]);
    },
    onLadderStockClick(stock) {
      if (this.ladderLongPressTriggered) {
        this.ladderLongPressTriggered = false;
        return;
      }
      this.toggleCartStock(stock);
    },
    openLadderContextMenu(event, stock) {
      const x = Math.min(event.clientX || 0, window.innerWidth - 140);
      const y = Math.min(event.clientY || 0, window.innerHeight - 60);
      this.ladderContextMenu = {
        visible: true,
        x,
        y,
        stock
      };
    },
    closeLadderContextMenu() {
      if (this.ladderContextMenu.visible) {
        this.ladderContextMenu = { visible: false, x: 0, y: 0, stock: null };
      }
    },
    clearLadderLongPress() {
      if (this.ladderLongPressTimer) {
        clearTimeout(this.ladderLongPressTimer);
        this.ladderLongPressTimer = null;
      }
    },
    onLadderTouchStart(event, stock) {
      this.clearLadderLongPress();
      this.ladderLongPressTriggered = false;
      const touch = event.touches && event.touches[0];
      if (!touch) return;
      const x = touch.clientX;
      const y = touch.clientY;
      this.ladderLongPressTimer = setTimeout(() => {
        this.ladderLongPressTriggered = true;
        this.openLadderContextMenu({ clientX: x, clientY: y }, stock);
      }, 500);
    },
    onLadderTouchMove() {
      this.clearLadderLongPress();
    },
    onLadderTouchEnd() {
      this.clearLadderLongPress();
    },
    async addStockToRecordLibrary() {
      const stock = this.ladderContextMenu.stock;
      this.closeLadderContextMenu();
      if (!stock) return;
      if (!this.selectedTradeDate) {
        this.$message.warning('请先选择日期');
        return;
      }
      try {
        const res = await stockApi.addRecordLibrary({
          code: this.stockCodeKey(stock),
          name: stock.name || '',
          tradeDate: this.selectedTradeDate,
          boards: Number(stock.boards) || 1
        });
        if (!res?.ok) {
          this.$message.warning(res?.message || '入记录库失败');
          return;
        }
        this.$message.success(
          `已入记录库：${res.data?.name || stock.name}（首板 ${res.data?.firstBoardDate || '-'}）`
        );
      } catch (error) {
        const msg = error?.message || error?.data?.message || '入记录库失败';
        this.$message.warning(msg);
      }
    },
    async addStockToAuctionTopLibrary(tag) {
      const stock = this.ladderContextMenu.stock;
      this.closeLadderContextMenu();
      if (!stock) return;
      if (!this.selectedTradeDate) {
        this.$message.warning('请先选择日期');
        return;
      }
      const topTag = String(tag || '').trim().toUpperCase();
      if (topTag !== 'T0' && topTag !== 'T1') {
        this.$message.warning('请选择 T0 或 T1');
        return;
      }
      try {
        const res = await stockApi.addAuctionTopLibrary({
          code: this.stockCodeKey(stock),
          name: stock.name || '',
          tradeDate: this.selectedTradeDate,
          boards: Number(stock.boards) || 1,
          tag: topTag
        });
        if (!res?.ok) {
          this.$message.warning(res?.message || '入TOP库失败');
          return;
        }
        this.$message.success(
          `已入TOP库 ${topTag}：${res.data?.name || stock.name}（${res.data?.tradeDate || this.selectedTradeDate}）`
        );
      } catch (error) {
        const msg = error?.message || error?.data?.message || '入TOP库失败';
        this.$message.warning(msg);
      }
    },
    openRecordLibraryDialog() {
      this.recordLibraryVisible = true;
    },
    onRecordLibraryDialogOpen() {
      this.loadRecordLibrary();
      this.loadGapUpLibrary();
    },
    async loadGapUpLibrary() {
      this.gapUpLibraryLoading = true;
      try {
        const res = await stockApi.fetchGapUpLibrary();
        this.gapUpLibraryList = Array.isArray(res?.data) ? res.data : [];
      } catch (_e) {
        this.gapUpLibraryList = [];
        this.$message.error('加载补涨库失败');
      } finally {
        this.gapUpLibraryLoading = false;
      }
    },
    onRecordLibraryScoreVersionChange(version) {
      this.recordLibraryScoreVersion =
        version === 'V1' || version === 'V2' || version === 'V3' || version === 'V4' || version === 'V5'
          ? version
          : 'V5';
      this.loadRecordLibrary();
    },
    async loadRecordLibrary() {
      this.recordLibraryLoading = true;
      this.recordLibraryEditingId = '';
      try {
        const res = await stockApi.fetchRecordLibrary({
          scoreVersion: this.recordLibraryScoreVersion
        });
        this.recordLibraryList = Array.isArray(res?.data)
          ? res.data.map((row) => ({
              ...row,
              finalBoards: row.finalBoards != null ? row.finalBoards : 1,
              type: row.type || '',
              _editFinalBoards: '',
              _editType: '',
              _saving: false
            }))
          : [];
      } catch (_e) {
        this.recordLibraryList = [];
        this.$message.error('加载记录库失败');
      } finally {
        this.recordLibraryLoading = false;
      }
    },
    isRecordLibraryEditing(row) {
      return !!row?.id && this.recordLibraryEditingId === row.id;
    },
    startRecordLibraryEdit(row) {
      if (!row?.id) return;
      // 同时只编辑一行
      this.recordLibraryList.forEach((item) => {
        if (item.id !== row.id) {
          this.$set(item, '_editFinalBoards', '');
          this.$set(item, '_editType', '');
        }
      });
      this.$set(row, '_editFinalBoards', row.finalBoards != null ? String(row.finalBoards) : '1');
      this.$set(row, '_editType', row.type || '');
      this.recordLibraryEditingId = row.id;
    },
    async saveRecordLibraryEdit(row) {
      if (!row?.id || !this.isRecordLibraryEditing(row)) return;
      const n = Number(row._editFinalBoards);
      if (!Number.isFinite(n) || n < 1) {
        this.$message.warning('最终连板高度须为 >=1 的数字');
        return;
      }
      const type = String(row._editType || '').trim();
      if (type && type !== '龙头' && type !== '补涨') {
        this.$message.warning('类型仅支持：龙头 / 补涨');
        return;
      }
      row._saving = true;
      try {
        const res = await stockApi.updateRecordLibrary(row.id, {
          finalBoards: Math.floor(n),
          type
        });
        if (!res?.ok) {
          this.$message.error(res?.message || '保存失败');
          return;
        }
        row.finalBoards = res.data?.finalBoards != null ? res.data.finalBoards : Math.floor(n);
        row.type = res.data?.type != null ? res.data.type : type;
        row._editFinalBoards = '';
        row._editType = '';
        this.recordLibraryEditingId = '';
        this.$message.success('已保存');
      } catch (_e) {
        this.$message.error('保存失败');
      } finally {
        row._saving = false;
      }
    },
    async removeRecordLibraryItem(row) {
      if (!row?.id) return;
      if (this.isRecordLibraryEditing(row)) {
        this.recordLibraryEditingId = '';
      }
      try {
        await this.$confirm(`确认删除 ${row.name || row.code}？`, '提示', { type: 'warning' });
      } catch (_e) {
        return;
      }
      try {
        const res = await stockApi.deleteRecordLibrary(row.id);
        if (!res?.ok) {
          this.$message.error(res?.message || '删除失败');
          return;
        }
        this.recordLibraryList = this.recordLibraryList.filter((item) => item.id !== row.id);
        this.$message.success('已删除');
      } catch (_e) {
        this.$message.error('删除失败');
      }
    },
    removeFromCart(code) {
      const key = String(code || '').trim().split('.')[0];
      this.cartStocks = this.cartStocks.filter((s) => this.stockCodeKey(s) !== key);
    },
    async openCartDialog() {
      if (!this.selectedTradeDate) {
        this.$message.warning('请先选择日期');
        return;
      }
      const owner = this.defaultCartOwner();
      if (!(await this.switchCartOwner(owner))) return;
      this.cartDialogVisible = true;
    },
    async openCartDialogForDate(td, idx, rowKey) {
      const nextOwner = this.cartOwnerFromRowKey(rowKey);
      if (td !== this.selectedTradeDate || idx !== this.selectedDateIndex) {
        await this.selectDate(td, idx);
        if (this.selectedTradeDate !== td) return;
      }
      if (!(await this.switchCartOwner(nextOwner))) return;
      this.cartDialogVisible = true;
    },
    onCartDialogClosed() {
      const own = this.loggedInReviewOwner;
      if (this.cartOwner !== own) {
        this.cartOwner = own;
        this.loadCartFromNote(this.selectedDateIndex);
      }
    },
    buildCartSavePayload() {
      const stocksKey = this.pickedStocksFieldKeyForOwner(this.cartOwner);
      const noteKey = this.reviewNoteFieldKeyForOwner(this.cartOwner);
      const payload = {
        tradeDate: this.selectedTradeDate,
        [stocksKey]: this.sortCartStocks(this.cartStocks)
      };
      payload[noteKey] = String(this.cartReviewThought || '').trim();
      return payload;
    },
    async saveCartToDb() {
      if (!this.selectedTradeDate) return;
      if (this.cartReadOnly) {
        this.$message.warning(`只能编辑${this.cartOwnerLabel(this.loggedInReviewOwner)}的复盘笔记`);
        return;
      }
      this.saveCartLoading = true;
      try {
        const payload = this.buildCartSavePayload();
        const res = await stockApi.saveReviewDayNote(payload);
        const saved = res?.note || {};
        const idx = this.selectedDateIndex;
        const stocksKey = this.pickedStocksFieldKeyForOwner(this.cartOwner);
        const noteKey = this.reviewNoteFieldKeyForOwner(this.cartOwner);
        if (idx >= 0) {
          this.$set(this.series.notes, idx, {
            ...(this.series.notes[idx] || { tradeDate: this.selectedTradeDate }),
            [stocksKey]: saved[stocksKey] || payload[stocksKey],
            [noteKey]: saved[noteKey] ?? payload[noteKey] ?? this.series.notes[idx]?.[noteKey] ?? ''
          });
        }
        this.cartStocks = this.sortCartStocks(saved[stocksKey] || payload[stocksKey] || []).map((s) => ({ ...s }));
        this.cartReviewThought = String(saved[noteKey] ?? payload[noteKey] ?? '').trim();
        this.cartSavedState = this.serializeCartState();
        this.cartDialogVisible = false;
        this.$message.success('复盘已保存');
      } catch (e) {
        this.$message.error(e?.response?.data?.message || e?.message || '保存失败');
      } finally {
        this.saveCartLoading = false;
      }
    },
    async bootstrapFromRoute() {
      const q = this.$route.query || {};
      if (q.startDate) this.startDate = String(q.startDate);
      if (q.endDate) this.endDate = String(q.endDate);
      await this.handleQuery();
    },
    async applyRouteQuery() {
      const q = this.$route.query || {};
      if (q.startDate) this.startDate = String(q.startDate);
      if (q.endDate) this.endDate = String(q.endDate);
      const tradeDate = q.tradeDate ? String(q.tradeDate) : '';
      const openCart = q.openCart === '1' || q.openCart === 'true';
      const cartOwner = q.cartOwner === '2' ? '2' : q.cartOwner === '1' ? '1' : '';
      if (openCart && tradeDate) {
        await this.handleQuery();
      }
      if (tradeDate && this.hasQueried) {
        const idx = this.series.tradeDates.indexOf(tradeDate);
        if (idx >= 0) {
          await this.selectDate(tradeDate, idx);
        }
      }
      if (cartOwner) {
        await this.switchCartOwner(cartOwner);
      }
      if (openCart && this.selectedTradeDate) {
        this.cartDialogVisible = true;
      }
      if (tradeDate || openCart || cartOwner || q.startDate || q.endDate) {
        const nextQuery = { ...q };
        delete nextQuery.tradeDate;
        delete nextQuery.openCart;
        delete nextQuery.cartOwner;
        delete nextQuery.startDate;
        delete nextQuery.endDate;
        this.$router.replace({ query: nextQuery }).catch(() => {});
      }
    },
    goToPreFirstBoardPick() {
      if (!this.selectedTradeDate) {
        this.$message.warning('请先选择日期');
        return;
      }
      if (this.cartReadOnly) {
        this.$message.warning(`只能编辑${this.cartOwnerLabel(this.loggedInReviewOwner)}的复盘笔记`);
        return;
      }
      // keep-alive + append-to-body：不关弹窗会叠在首板预选页上
      this.cartDialogVisible = false;
      this.$router.push({
        name: 'FirstBoardPreselection',
        query: {
          mode: 'pick',
          tradeDate: this.selectedTradeDate,
          owner: this.cartOwner,
          returnTo: '/review-notes',
          startDate: this.startDate,
          endDate: this.endDate
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$bg-page: #f5f7fa;
$border: #ebeef5;
$text: #303133;
$text-secondary: #606266;
$text-muted: #909399;
$primary: #409eff;
$radius: 8px;

.review-notes {
  min-height: 100%;
  padding: 16px;
  box-sizing: border-box;
  background: $bg-page;
}

.panel {
  background: #fff;
  border-radius: $radius;
  border: 1px solid $border;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 16px 20px;
  margin-bottom: 16px;
}

.panel-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;

  &--table {
    margin-top: 8px;
    padding-top: 16px;
    border-top: 1px dashed $border;
  }

  &--collapsible {
    cursor: pointer;
    user-select: none;
    align-items: center;
    margin-bottom: 10px;

    &:active {
      opacity: 0.85;
    }
  }
}

.panel-head-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.cart-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #dcdfe6;
  background: #fff;
  color: $text;
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 16px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;

  &:hover {
    border-color: $primary;
    color: $primary;
  }

  i {
    font-size: 14px;
  }
}

.cart-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: $primary;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.cart-dirty-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e6a23c;
  margin-left: 2px;
}

.panel-collapse-ctrl {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: $text-muted;

  i {
    font-size: 14px;
  }
}

.panel-collapse-hint {
  white-space: nowrap;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: $text;
  padding-left: 10px;
  border-left: 3px solid $primary;
  line-height: 1.2;
}

.panel-title-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.record-library-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid $primary;
  background: $primary;
  color: #fff;
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 14px;
  cursor: pointer;
  transition: opacity 0.2s, background 0.2s;

  &:hover {
    opacity: 0.9;
    background: darken($primary, 4%);
  }

  i {
    font-size: 13px;
  }
}

.panel-sub {
  font-size: 13px;
  color: $text-muted;
}

.panel-hint {
  font-size: 12px;
  color: $text-muted;
}

.toolbar-panel {
  margin-bottom: 16px;

  .panel-title {
    margin-bottom: 12px;
  }

  &--mobile .panel-title {
    margin-bottom: 8px;
  }
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  &--mobile {
    flex-direction: column;
    align-items: stretch;
  }
}

.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-label {
  font-size: 13px;
  color: $text-secondary;
  white-space: nowrap;
}

.toolbar-date {
  width: 148px;
}

.toolbar-sync-btn {
  width: 100%;
}

.toolbar-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  width: 100%;
}

.toolbar-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.toolbar-actions {
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 12px;

  .el-button {
    flex: 1;
    margin: 0;
  }
}

.toolbar-date--fluid {
  width: 100% !important;
}

.main-layout {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.content-panel {
  margin-bottom: 16px;
  overflow: visible;
}

.charts-area {
  height: 620px;
  width: 100%;
  margin-bottom: 4px;
  padding-right: 8px;
  box-sizing: border-box;
  border-radius: 6px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  overflow: visible;

  &--mobile {
    height: 240px;
    margin-bottom: 0;
    padding: 0 4px;
    background: #fff;
    border-radius: 10px;
  }
}

.chart-tab-pills {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  padding: 4px;
  background: #f0f2f5;
  border-radius: 10px;
}

.chart-tab-pill {
  flex: 1;
  border: none;
  background: transparent;
  color: $text-secondary;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &.is-active {
    background: #fff;
    color: $primary;
    font-weight: 600;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
}

.review-table-wrap {
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid $border;
}

.review-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    border-bottom: 1px solid $border;
    border-right: 1px solid $border;
    padding: 10px 12px;
    text-align: center;
    min-width: 72px;
    cursor: pointer;
    transition: background 0.15s;

    &:last-child {
      border-right: none;
    }
  }

  thead th {
    background: #f8f9fb;
    color: $text-secondary;
    font-weight: 500;
    font-size: 12px;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody td {
    vertical-align: middle;

    span {
      white-space: pre-line;
      line-height: 1.45;
    }
  }

  tbody tr:hover td:not(.review-table-label-col) {
    background: #f5f9ff;
  }

  .review-table-label-col {
    min-width: 96px;
    background: #f8f9fb;
    font-weight: 500;
    color: $text;
    text-align: left;
    position: sticky;
    left: 0;
    z-index: 1;
    border-right: 1px solid #e4e7ed;
  }

  td.is-emphasis .review-table-cell-text {
    color: #e53935;
    font-weight: 700;
  }

  .date-head {
    display: inline-block;
    min-width: 36px;
  }

  .is-selected {
    background: #ecf5ff !important;
    box-shadow: inset 0 -2px 0 $primary;
  }

  .is-empty {
    color: #c0c4cc;
  }

  .is-cart-link {
    color: $primary;

    &:hover {
      background: #ecf5ff !important;
    }
  }

  .is-cart-readonly {
    color: $text-secondary;

    &:hover {
      background: #fafafa !important;
    }
  }
}

.ladder-toolbar {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid $border;
}

.ladder-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  &--mobile {
    gap: 8px;
  }
}

.filter-rel-vol-label {
  margin-left: 4px;
  color: $text-secondary;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.filter-chip-band-select {
  width: 140px;
  margin-right: 6px;
}
.filter-rel-vol-group {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.filter-chip {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: $text-secondary;
  font-size: 12px;
  padding: 7px 12px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1;

  &.is-active {
    background: #ecf5ff;
    border-color: #b3d8ff;
    color: $primary;
    font-weight: 600;
  }

  &--action {
    background: #fff;
    border-style: dashed;
    color: $primary;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.theme-strip {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 4px;
  scrollbar-width: thin;
}

.theme-chip {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  background: #f4f4f5;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 88px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &--hot {
    background: linear-gradient(135deg, #fff1f0 0%, #ffe7e6 100%);
    border-color: #ffccc7;

    .theme-chip-name { color: #cf1322; }
  }

  &--warm {
    background: linear-gradient(135deg, #fff7e6 0%, #ffecc7 100%);
    border-color: #ffd591;

    .theme-chip-name { color: #d48806; }
  }

  &.is-active {
    background: linear-gradient(135deg, #ecf5ff 0%, #f0f7ff 100%);
    border-color: $primary;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);

    .theme-chip-name { color: $primary; }
  }
}

.theme-chip-name {
  font-weight: 600;
  color: $text;
  white-space: nowrap;
}

.theme-chip-meta {
  color: $text-muted;
  font-size: 11px;
  white-space: nowrap;
}

.ladder-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.ladder-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: $text;
  margin-bottom: 10px;
}

.node-star-mark {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 2px;
  line-height: 1;
}

.node-star--market {
  color: #f56c6c;
}

.node-star--dragon {
  color: #409eff;
}

.node-text--market {
  color: #f56c6c;
  font-weight: 700;
}

.node-text--dragon {
  color: #409eff;
  font-weight: 700;
}

.review-table-node-cell {
  white-space: pre-wrap;
  word-break: break-word;
}

.node-star-mark--sm {
  font-size: 18px;
  letter-spacing: 1px;
}

.ladder-board-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.ladder-stocks {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(152px, 1fr));
  gap: 10px;
}

.ladder-stock {
  border: 1px solid $border;
  border-radius: 8px;
  -webkit-touch-callout: none;
  user-select: none;
  padding: 10px 12px;
  background: #fafbfc;
  transition: all 0.2s;
  position: relative;
  cursor: pointer;
  user-select: none;

  &:hover {
    border-color: #c6e2ff;
    background: #fff;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }

  &.is-in-cart {
    border-color: #409eff;
    background: linear-gradient(180deg, #ecf5ff 0%, #fff 100%);
    box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.2);
  }
}

.ladder-stock-check {
  position: absolute;
  top: -1px;
  right: -1px;
  z-index: 1;
  width: 18px;
  height: 18px;
  border-radius: 0 8px 0 8px;
  background: $primary;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  line-height: 1;
  pointer-events: none;
}

.ladder-stock-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
}

.ladder-stock-name {
  flex: 1 1 auto;
  min-width: 0;
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: $text;
  line-height: 1.35;
  white-space: nowrap;
  overflow: visible;
  user-select: text;
  cursor: text;
  -webkit-user-select: text;
}

.ladder-stock-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.ladder-stock-time {
  flex-shrink: 0;
  font-size: 11px;
  color: $text-muted;
  background: #f0f2f5;
  padding: 1px 6px;
  border-radius: 4px;
  text-align: center;
}

.ladder-stock-score {
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 11px;
  font-weight: 600;
  color: #cf1322;
  background: #fff1f0;
  padding: 1px 6px;
  border-radius: 4px;
  line-height: 1.35;
}

.ladder-stock-theme {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: $text-muted;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ladder-empty,
.empty-placeholder {
  text-align: center;
  color: $text-muted;
  padding: 40px 16px;

  i {
    font-size: 40px;
    color: #dcdfe6;
    display: block;
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

.mobile-day-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0 4px;
  padding: 10px 12px;
  background: #f8f9fb;
  border-radius: 10px;
}

.day-nav-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #fff;
  color: $text;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  }

  &:not(:disabled):active {
    transform: scale(0.96);
    background: #ecf5ff;
    color: $primary;
  }
}

.mobile-day-center {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.mobile-day-label {
  display: block;
  font-weight: 600;
  font-size: 15px;
  color: $text;
}

.mobile-day-hint {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: $text-muted;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-review-collapsed {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mobile-review-expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  padding: 10px;
  border: 1px dashed #dcdfe6;
  border-radius: 10px;
  background: #fafbfc;
  color: $primary;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:active {
    background: #ecf5ff;
    border-color: #b3d8ff;
  }
}

.day-card {
  position: relative;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid $border;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.2s;

  &.is-selected {
    border-color: #b3d8ff;
    background: linear-gradient(180deg, #f5f9ff 0%, #fff 100%);
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.12);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 12px;
      bottom: 12px;
      width: 3px;
      border-radius: 0 2px 2px 0;
      background: $primary;
    }
  }

  &--compact {
    cursor: pointer;
    border-color: #b3d8ff;
    background: linear-gradient(180deg, #f5f9ff 0%, #fff 100%);
    box-shadow: 0 1px 4px rgba(64, 158, 255, 0.1);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 12px;
      bottom: 12px;
      width: 3px;
      border-radius: 0 2px 2px 0;
      background: $primary;
    }

    .day-card__head {
      margin-bottom: 8px;
    }

    .day-card__note {
      margin-bottom: 0;
    }
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__date {
    font-size: 18px;
    font-weight: 700;
    color: $text;
    letter-spacing: -0.02em;
  }

  &__full {
    font-size: 11px;
    color: $text-muted;
  }

  &__metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__note {
    font-size: 12px;
    color: $text-secondary;
    line-height: 1.5;
    padding: 8px 10px;
    margin-bottom: 8px;
    background: #f8f9fb;
    border-radius: 8px;
  }

  &__edit {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: $primary;
    font-size: 13px;
    font-weight: 500;
    padding: 4px 0;
    cursor: pointer;

    i {
      font-size: 14px;
    }
  }
}

.metric-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 8px;

  em {
    font-style: normal;
    font-size: 11px;
    font-weight: 500;
    opacity: 0.75;
  }

  &--temp {
    background: rgba(91, 143, 249, 0.1);
    color: #3d7ee8;
  }

  &--height {
    background: rgba(246, 189, 22, 0.12);
    color: #d4a012;
  }

  &--index {
    background: rgba(232, 100, 82, 0.1);
    color: #d9534f;
  }
}

.mobile-drawer-body {
  padding: 0 16px 80px;
}

.mobile-drawer-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid $border;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
  z-index: 10;

  .el-button {
    flex: 1;
    margin: 0;
  }
}

.cart-dialog-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 0 4px;
}

.cart-dialog-date {
  font-size: 13px;
  color: $text-muted;
  margin-bottom: 16px;
  text-align: center;
}

.cart-readonly-tip {
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdf6ec;
  color: #e6a23c;
  font-size: 13px;
  text-align: center;
}

.cart-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.cart-section-title {
  font-size: 14px;
  font-weight: 600;
  color: $text;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid $border;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cart-section-sub {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: $text-muted;
}

.cart-node-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cart-node-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
}

.cart-node-tag--market {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.1);
  border: 1px solid rgba(245, 108, 108, 0.25);
}

.cart-node-tag--dragon {
  color: #409eff;
  background: rgba(64, 158, 255, 0.1);
  border: 1px solid rgba(64, 158, 255, 0.25);
}

.cart-section-title--prefirst {
  justify-content: space-between;
}

.cart-ladder-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ladder-group--cart {
  margin-bottom: 0;
}

.ladder-stock--cart {
  cursor: default;
}

.cart-item-remove--inline {
  position: static;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    color: #f56c6c;
  }
}

.edit-node-item--market :deep(.el-checkbox__label) {
  color: #f56c6c;
}

.edit-node-item--dragon :deep(.el-checkbox__label) {
  color: #409eff;
}

.cart-node-empty {
  font-size: 13px;
  color: $text-muted;
  padding: 8px 0;
}

.node-factor-toolbar {
  margin: 0 0 12px;
}

.node-factor-detail {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: #909399;
  word-break: break-all;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.review-label-with-help {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.greedy-node-help-icon {
  font-size: 14px;
  color: #c0c4cc;
  cursor: help;

  &:hover {
    color: #409eff;
  }
}

.greedy-node-hit-tag {
  cursor: help;
  border-bottom: 1px dotted currentColor;
}

.review-table-greedy-nodes {
  font-size: 12px;
  line-height: 1.45;
  word-break: break-all;

  &.is-hit {
    color: #409eff;
  }
}

.node-factor-meta {
  font-size: 13px;
  color: #606266;
}

.node-factor-hint {
  margin-left: 6px;
  font-size: 12px;
  color: #909399;
}

.node-factor-meta b {
  color: #303133;
  margin: 0 2px;
}

.node-factor-input {
  width: 100%;
}

.note-edit-dialog {
  .node-factor-grid {
    .el-form-item__label {
      white-space: nowrap;
    }
  }
}

.edit-node-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.edit-node-item {
  margin-right: 0 !important;
}

.cart-empty--inline {
  padding: 24px 16px;
}

.cart-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cart-empty {
  text-align: center;
  color: $text-muted;
  padding: 48px 16px;

  i {
    font-size: 36px;
    color: #dcdfe6;
    display: block;
    margin-bottom: 10px;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

.cart-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cart-item {
  position: relative;
  padding: 12px 36px 12px 12px;
  border: 1px solid $border;
  border-radius: 10px;
  background: #fafbfc;
}

.cart-item-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.cart-item-name {
  font-weight: 600;
  font-size: 14px;
  color: $text;
}

.cart-item-boards {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #d48806;
  background: rgba(246, 189, 22, 0.12);
  padding: 2px 8px;
  border-radius: 10px;
}

.cart-item-badges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.cart-item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: $text-muted;
}

.cart-item-remove {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: #f0f2f5;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    background: #fde2e2;
    color: #f56c6c;
  }
}

@media screen and (max-width: 768px) {
  .review-notes {
    padding: 10px 10px 20px;
    background: #eef1f6;

    &--mobile {
      .panel {
        padding: 14px;
        margin-bottom: 10px;
        border-radius: 12px;
        border: none;
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
      }

      .panel-head {
        margin-bottom: 10px;
        flex-wrap: wrap;

        &--table {
          margin-top: 4px;
          padding-top: 14px;
        }

        .panel-head-right {
          width: 100%;
          justify-content: space-between;
          margin-top: 4px;
        }
      }

      .cart-trigger {
        font-size: 13px;
        padding: 6px 12px;
      }

      .panel-title {
        font-size: 16px;
        padding-left: 8px;
        border-left-width: 4px;
      }

      .panel-sub {
        font-size: 12px;
      }

      .toolbar-panel .panel-title {
        margin-bottom: 10px;
      }

      .ladder-toolbar {
        margin-bottom: 10px;
        padding-bottom: 10px;
      }

      .theme-strip {
        margin-bottom: 12px;
        padding-bottom: 2px;
        -webkit-overflow-scrolling: touch;
      }

      .theme-chip {
        min-width: 96px;
        padding: 10px 14px;
        border-radius: 10px;
      }

      .ladder-group {
        margin-bottom: 14px;
      }

      .ladder-group-title {
        font-size: 14px;
        margin-bottom: 8px;
      }

      .ladder-board-badge {
        min-width: 26px;
        height: 26px;
        font-size: 13px;
        border-radius: 8px;
      }

      .ladder-stock {
        padding: 12px;
        border-radius: 10px;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        border-color: #eef0f3;

        &:active {
          transform: scale(0.98);
        }
      }

      .ladder-stock-name {
        font-size: 15px;
      }

      .ladder-stock-time {
        font-size: 10px;
        background: #f5f7fa;
      }

      .ladder-empty,
      .empty-placeholder {
        padding: 32px 16px;
      }
    }
  }

  .ladder-stocks {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
}

.ladder-context-menu {
  position: fixed;
  z-index: 4000;
  min-width: 120px;
  padding: 4px 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

  &__item {
    display: block;
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: 8px 14px;
    font-size: 13px;
    color: #303133;
    cursor: pointer;

    &:hover,
    &:active {
      background: #f5f7fa;
      color: #409eff;
    }
  }
}
</style>

<style lang="scss">
.review-drawer {
  border-radius: 16px 16px 0 0;

  .el-drawer__header {
    margin-bottom: 0;
    padding: 16px 16px 12px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    border-bottom: 1px solid #ebeef5;
  }

  .el-drawer__body {
    padding: 16px 0 0;
  }
}

.review-cart-dialog {
  .el-dialog__header {
    padding-bottom: 8px;
  }

  .el-dialog__body {
    padding-top: 8px;
  }

  .el-dialog__footer {
    padding-top: 8px;
    border-top: 1px solid #ebeef5;
  }
}

.record-library-dialog {
  .el-dialog__body {
    padding-top: 8px;
    overflow-x: auto;
    min-width: 0;
  }

  .el-table {
    width: 100% !important;
  }

  .el-table__body-wrapper {
    overflow-x: auto !important;
  }
}

.greedy-node-catalog-popover {
  padding: 0 !important;

  .greedy-node-catalog {
    max-height: 400px;
    overflow-y: auto;
    padding: 12px 14px;
  }

  .greedy-node-catalog__head {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
  }

  .greedy-node-catalog__item:not(:last-child) {
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #ebeef5;
  }

  .greedy-node-catalog__title {
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 4px;
  }

  .greedy-node-catalog__weight {
    color: #409eff;
    margin: 0 6px 0 2px;
  }

  .greedy-node-catalog__factor {
    color: #606266;
  }

  .greedy-node-catalog__rule {
    font-size: 12px;
    line-height: 1.55;
    color: #909399;
  }
}

.greedy-node-hit-tooltip {
  max-width: 360px;
  line-height: 1.55;
}

.greedy-node-tooltip-title {
  font-weight: 600;
  margin-bottom: 4px;
}
</style>
